// core/_requests.ts
import axios from 'axios'
import type { AxiosError } from 'axios'
import { formatApiError } from '../../../shared/utils/apiErrorMessage'

import type {
  DocumentItem,
  Vessel,
  ManualPlanDto,
  ManualPlanSparePartOption,
  Certificate,
  CertificateRevision,
  CreateInspectionPayload,
  CreatePlansPayload,
  InspectionDto,
  InspectionAttachmentInfo,
  InspectionPlanDto,
  InspectionRecord,
  QhseInspectionKind,
  Port,
  QHSEDefectDto,
  DefectRecord,
  DefectCategory,
  TpiSubCategory,
  SmsCode,
  DefectDepartment,
  DefectStatus,
  DefectClosureResult,
  RequisitionLinkOption,
  QHSEInspectionFindingDto,
  QHSEAuditFindingDto,
  InspectionFindingAttachmentInfo,
  FindingStatus,
  AuditPlanDto,
  AuditDto,
  AuditRecord,
  QhseAuditKind,
  CreateAuditPayload,
  CreateAuditPlansPayload,
  AuditAttachmentInfo,
  QHSENearMissReportDto,
  NearMissRecord,
  NearMissStatus,
  NearMissClosureResult,
  NearMissOccurrenceType,
  NearMissLocationOfOccurrence,
  NearMissSubstandardAct,
  NearMissSubstandardCondition,
  AuditFindingAttachmentInfo,
  QHSESafetyCommitteeMeetingDto,
  SafetyCommitteeStatus,
  SafetyCommitteeFindingLinkType,
  QHSEWorkPermitDto,
  WorkPermitAttachmentInfo,
  RiskAssessmentDto,
  RiskAssessmentRecord,
  RiskAssessmentRevision,
  RiskLibraryCategory,
  RiskLibraryTaskSummary,
  RiskLibraryRiskNumberOption,
  RiskLibraryTaskDetail,
  RiskLibraryCompanyHeader,
  RiskLibraryTaskWritePayload,
  RiskLibraryVesselOption,
  CompanyGroupAdminLite,
  CertificateCategory,
  MainEngineConsumptionDto,
  MainEngineTrialType,
  MainEngineTrialAttachmentDto,
  AuxiliaryEngineReportDto,
  FreshWaterGeneratorReportDto,
  BwtsReportDto,
  PurifierReportDto,
  InertGasSystemReportDto,
  CompressorSystemReportDto,
  BoilerPerformanceReportDto,
} from './_models'

type DownloadPdfResult =
  | { type: 'url'; url: string | null }
  | { type: 'blob'; blob: Blob }

// ===============================
// API CONFIG – BASE URLS
// ===============================
const API_URL = process.env.REACT_APP_API_URL

// Documents & Manuals
const DOCUMENTS_API_URL = `${API_URL}/qhse/documents`
const MP_API_URL = `${API_URL}/qhse/manuals-plans`

// Findings
const INSPECTION_FINDING_API_URL = `${API_URL}/qhse/inspection-findings`
const AUDIT_FINDING_API_URL = `${API_URL}/qhse/audit-findings`

// Certificates
const CERT_API_URL = `${API_URL}/qhse/certificates`
const CERT_CATEGORY_API_URL = `${CERT_API_URL}/categories`
const CERT_CATEGORY_LIBRARY_API_URL = `${CERT_CATEGORY_API_URL}/library`
const OFFICE_CERT_API_URL = `${API_URL}/qhse/office-certificates`
// Risk Assessment
const RISK_API_URL = `${API_URL}/qhse/risk-assessments`
// Risk Assessment library (SAF master list / worksheets — not vessel forms)
const RISK_LIBRARY_API_URL = `${API_URL}/qhse/risk-assessment-library`

// Inspection / Audit Core
const INS_API_URL = `${API_URL}/qhse`
const INS_KIND_API = `${INS_API_URL}/inspection-kinds`
const INS_PLAN_API = `${INS_API_URL}/inspection-plans`
const INS_INDEX_API = `${INS_API_URL}/inspections`
const VESSEL_API_URL = `${API_URL}/vessels`
const PORT_API_URL = `${API_URL}/ports`

const AUD_API_URL = `${API_URL}/qhse`
const AUD_KIND_API = `${AUD_API_URL}/audit-kinds`
const AUD_PLAN_API = `${AUD_API_URL}/audit-plans`
const AUD_INDEX_API = `${AUD_API_URL}/audits`

// Shared QHSE base
const QHSE_API = `${API_URL}/qhse`

// Defects
const DEFECT_API_URL = `${API_URL}/qhse/defects`
const REQUISITION_LINK_OPTIONS_URL = `${API_URL}/requisitions/link-options`

// Near Miss
const NEAR_MISS_API_URL = `${API_URL}/qhse/near-miss-reports`

// Safety Committee Meetings
const SCM_API_URL = `${API_URL}/qhse/safety-committee-meetings`

// Work Permits & Checklists
const WORK_PERMIT_API_URL = `${API_URL}/qhse/work-permits`

// ===============================
// HELPERS
// ===============================

/** util: convert "2.1 MB" -> bytes (rough) */
const sizeToBytes = (s?: string | null) => {
  if (!s) return 0
  const [numStr, unit] = s.trim().split(/\s+/)
  const n = parseFloat(numStr)
  if (!isFinite(n)) return 0
  const u = (unit || '').toUpperCase()
  if (u.startsWith('KB')) return Math.round(n * 1024)
  if (u.startsWith('MB')) return Math.round(n * 1024 * 1024)
  if (u.startsWith('GB')) return Math.round(n * 1024 * 1024 * 1024)
  if (u.startsWith('B')) return Math.round(n)
  return 0
}

/** Surfaces server messages; maps 401/403 to a clear permission-denied string. */
const pickAxiosMessage = (err: unknown, fallback: string) => formatApiError(err, fallback)

type PageResponse<T> = {
  content?: T[]
  totalElements?: number
  number?: number
  size?: number
}

export type CertificateCategoryPayload = {
  name: string
  isActive?: boolean | null
  scopeType?: 'GLOBAL' | 'COMPANY' | string
  companyGroupAdminId?: number | null
  targetType?: string
}

// ===============================
// LOOKUPS / LITE LISTS
// ===============================

// Minimal "lite" list for QHSE usage
export type { CrewLite } from './_models'
export async function listCrewsLite(vesselId?: number) {
  const params: any = {}
  if (vesselId != null && Number.isFinite(Number(vesselId))) {
    params.vesselId = Number(vesselId)
  }
  const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/crew/all`, { params })

  return (Array.isArray(data) ? data : [])
    .map((c: any) => {
      const rankId =
        Number(
          c?.rankInfo?.id ??
          c?.rankId ??
          c?.rank?.id
        )

      return {
        id: Number(c.id ?? c.userId ?? c.crewId),
        fullName: String(
          c.fullName ??
          c.name ??
          [c.firstName, c.lastName].filter(Boolean).join(' ') ??
          `User #${c.id}`
        ),
        rankName:
          c?.rankName ??
          c?.rank?.rank ??
          c?.rankInfo?.rank ??
          undefined,
        rankId: Number.isFinite(rankId) ? rankId : undefined,

        vesselId:
          c?.vessel?.id != null
            ? Number(c.vessel.id)
            : (c?.vesselId != null ? Number(c.vesselId) : undefined),

        companyGroupAdminId: (() => {
          const v =
            c.companyGroupAdminId ??
            c.companyGroupId ??
            c.cgaid?.id ??
            c.cga?.id ??
            c.vessel?.companyGroupAdminId ??
            c.vessel?.companyGroupAdmin?.id
          return Number.isFinite(Number(v)) ? Number(v) : undefined
        })(),
        companyAdminId: (() => {
          const v =
            c.companyAdminId ??
            c.companyId ??
            c.companyAdmin?.id ??
            c.vessel?.companyAdminId ??
            c.vessel?.companyAdmin?.id
          return Number.isFinite(Number(v)) ? Number(v) : undefined
        })(),

        active: Boolean(c?.active),     // 👈 include active flag
      }
    })
    .filter((c: any) => Number.isFinite(c.id))
}

// --- lookups ---
export async function listInspectionKinds(): Promise<QhseInspectionKind[]> {
  const { data } = await axios.get(`${INS_KIND_API}`)
  return data
}

// Use base endpoints like the rest of the app
export async function listPorts(): Promise<Port[]> {
  const { data } = await axios.get(`${API_URL}/ports/all`)
  const rawList = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.content)
    ? (data as any).content
    : []
  return rawList.map((p: any) => ({
    id: Number(p.id),
    name: String(p.main_port ?? p.Main_port ?? p.name ?? ''), // <- normalize to "name"
    latitude: p.latitude,
    longitude: p.longitude,
  }))
}

// --- Audit kinds (mirror inspection kinds) ---
export async function listAuditKinds(): Promise<QhseAuditKind[]> {
  const { data } = await axios.get(`${AUD_KIND_API}`)
  return data
}

// export const getVesselList = async (): Promise<Vessel[]> => {
//   try {
//     const response = await axios.get(VESSEL_API_URL, {
//       headers: {
//       },
//     });

//     return response.data;

//   } catch (error: any) {
//     console.error('Error fetching vessel list:', error);
//     throw new Error(error.response?.data?.message || 'Error fetching vessel list');
//   }
// };

// ===============================
// DOCUMENT APIS
// ===============================

// 1. View document (PDF / URL / Blob)
export async function viewPdfDocumentRequest(token: string, documentId: number) {
  const response = await fetch(
    `${DOCUMENTS_API_URL}/view/${documentId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to load PDF: ${response.status} ${response.statusText}`)
  }

  const contentType = response.headers.get('content-type') || ''

  if (contentType.includes('application/json')) {
    // server gives us a JSON with download URL
    const data = await response.json()
    return {
      type: 'url',
      url:
        data.url ||
        data.downloadUrl ||
        data.fileUrl ||
        (data.data && data.data.url) ||
        null,
    }
  } else if (contentType.includes('application/pdf')) {
    // server gives us PDF blob
    const blob = await response.blob()
    return {
      type: 'blob',
      blob,
    }
  } else {
    // unknown → try JSON fallback
    try {
      const data = await response.json()
      return {
        type: 'url',
        url:
          data.url ||
          data.downloadUrl ||
          data.fileUrl ||
          (data.data && data.data.url) ||
          null,
      }
    } catch {
      const blob = await response.blob()
      return {
        type: 'blob',
        blob,
      }
    }
  }
}

// 2. Download document (returns URL from JSON)
export async function downloadPdfDocumentRequest(token: string, documentId: number) {
  const response = await fetch(`${DOCUMENTS_API_URL}/download/${documentId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`Download failed: ${response.status} ${response.statusText}`)
  }

  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    const data = await response.json()
    const url =
      data.url ||
      data.downloadUrl ||
      data.fileUrl ||
      (data.data && data.data.url) ||
      null
    return { type: 'url', url }
  }

  const blob = await response.blob()
  return { type: 'blob', blob }
}

export async function fetchDocumentsRequest(
  compnayId: number,
  token: string
): Promise<DocumentItem[]> {
  if (!token) throw new Error('Missing auth token for documents fetch')
  const { data } = await axios.get<DocumentItem[]>(`${DOCUMENTS_API_URL}/company-group/${compnayId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return data
}

export async function createQhseFolderRequest(
  payload: {
    companyGroupId: number
    title: string
    category?: string
    createdBy?: string
    scopeType?: string
    vesselId?: number | null
    crewId?: number | null
  },
  token: string,
  parentId?: number | null
): Promise<DocumentItem> {
  const { data } = await axios.post(`${DOCUMENTS_API_URL}/folder`, payload, {
    params: parentId ? { parentId } : undefined,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return data
}

export async function uploadQhseDocumentsRequest(
  payload: {
    companyGroupId: number
    category?: string
    createdBy?: string
    scopeType?: string
    vesselId?: number | null
    crewId?: number | null
    files: File[]
  },
  token: string,
  parentId?: number | null
): Promise<DocumentItem[]> {
  const fd = new FormData()
  const dto = {
    companyGroupId: payload.companyGroupId,
    category: payload.category,
    createdBy: payload.createdBy,
    scopeType: payload.scopeType,
    vesselId: payload.vesselId ?? undefined,
    crewId: payload.crewId ?? undefined,
  }
  fd.append('dto', new Blob([JSON.stringify(dto)], { type: 'application/json' }))
  payload.files.forEach((file) => fd.append('files', file))

  const { data } = await axios.post(`${DOCUMENTS_API_URL}/upload-multiple`, fd, {
    params: parentId ? { parentId } : undefined,
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
    transformRequest: (d) => d,
    maxBodyLength: Infinity,
  })
  return data
}

export async function uploadQhseZipRequest(
  payload: {
    companyGroupId: number
    category?: string
    scopeType?: string
    vesselId?: number | null
    crewId?: number | null
    file: File
  },
  token: string,
  parentId?: number | null
): Promise<DocumentItem[]> {
  const fd = new FormData()
  fd.append('file', payload.file)
  if (payload.category) fd.append('category', payload.category)
  if (payload.companyGroupId) fd.append('companyGroupId', String(payload.companyGroupId))
  if (payload.scopeType) fd.append('scopeType', payload.scopeType)
  if (payload.vesselId != null) fd.append('vesselId', String(payload.vesselId))
  if (payload.crewId != null) fd.append('crewId', String(payload.crewId))

  const { data } = await axios.post(`${DOCUMENTS_API_URL}/upload-zip`, fd, {
    params: parentId ? { parentId } : undefined,
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
    withCredentials: false,
    transformRequest: (d) => d,
    maxBodyLength: Infinity,
  })
  return data
}

export async function deleteQhseDocumentRequest(
  documentId: number,
  token: string
): Promise<void> {
  await axios.delete(`${DOCUMENTS_API_URL}/${documentId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}

export async function updateQhseDocumentRequest(
  documentId: number,
  payload: Partial<DocumentItem>,
  token: string,
  parentId?: number | null
): Promise<DocumentItem> {
  const { data } = await axios.put(`${DOCUMENTS_API_URL}/${documentId}`, payload, {
    params: parentId ? { parentId } : undefined,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return data
}

// ===============================
// MANUALS & PLANS
// ===============================

/** 1) List by Company Group (server-paged) */
export async function listManualsByCompany(companyGroupId: number): Promise<ManualPlanDto[]> {
  try {
    const { data } = await axios.get<ManualPlanDto[]>(
      `${MP_API_URL}/company-group/${companyGroupId}`
    )
    return data
  } catch (err) {
    const ae = err as AxiosError<any>
    throw new Error(
      ae?.response?.data?.message ??
      ae?.response?.data?.error ??
      ae?.message ??
      'Failed to load manuals'
    )
  }
}

/** 1b) List by Company Group and Vessel */
export async function listManualsByCompanyAndVessel(
  companyGroupId: number,
  vesselId: number | null
): Promise<ManualPlanDto[]> {
  try {
    if (vesselId === null) {
      return listManualsByCompany(companyGroupId)
    }
    const { data } = await axios.get<ManualPlanDto[]>(
      `${MP_API_URL}/company-group/${companyGroupId}/vessel/${vesselId}`
    )
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Failed to load manuals'))
  }
}

/** 1c) List by Vessels (for multi-vessel filtering) */
export async function listManualsByVessels(vesselIds: number[]): Promise<ManualPlanDto[]> {
  try {
    const { data } = await axios.post<ManualPlanDto[]>(
      `${MP_API_URL}/vessels`,
      vesselIds
    )
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Failed to load manuals'))
  }
}

/** 2) Get by id */
export async function getManualPlan(id: number): Promise<ManualPlanDto> {
  try {
    const { data } = await axios.get<ManualPlanDto>(`${MP_API_URL}/${id}`)
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Failed to get manual/plan'))
  }
}

/** 3) Create (multipart) — SAME pattern as certificate upload */
export async function createManualPlan(payload: {
  companyGroupId: number
  name: string
  dateOfApproval?: string
  revisionNo?: string
  approvedBy?: string
  uploadedBy: string
  uploadedByName: string
  remarks?: string
  vesselId?: number | null
  sparePartIds?: number[]
  file: File
}) {
  const fd = new FormData()
  fd.append('companyGroupId', String(payload.companyGroupId))
  fd.append('name', payload.name)
  if (payload.dateOfApproval) fd.append('dateOfApproval', payload.dateOfApproval)
  if (payload.revisionNo) fd.append('revisionNo', payload.revisionNo)
  if (payload.approvedBy) fd.append('approvedBy', payload.approvedBy)
  fd.append('uploadedBy', payload.uploadedBy)
  fd.append('uploadedByName', payload.uploadedByName)
  if (payload.remarks) fd.append('remarks', payload.remarks)
  if (payload.vesselId) fd.append('vesselId', String(payload.vesselId))
  if (payload.sparePartIds && payload.sparePartIds.length > 0) {
    fd.append('sparePartIds', payload.sparePartIds.join(','))
  }
  fd.append('file', payload.file)

  // IMPORTANT: force multipart and skip any data transformers
  const { data } = await axios.post(`${MP_API_URL}`, fd, {
    headers: {
      // force correct content-type (will add boundary automatically for FormData)
      'Content-Type': 'multipart/form-data',
      // optional but harmless:
      Accept: 'application/json',
    },
    transformRequest: (d) => d,        // don't serialize FormData
    maxBodyLength: Infinity,           // avoid size caps
  })
  return data
}

/** 4) Update metadata (PUT JSON) */
export async function updateManualPlan(
  id: number,
  partial: Partial<ManualPlanDto>
): Promise<ManualPlanDto> {
  try {
    const { data } = await axios.put<ManualPlanDto>(`${MP_API_URL}/${id}`, partial)
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Update failed'))
  }
}

/** 5) Replace file only (PATCH multipart) */
export async function replaceManualPlanFile(id: number, file: File): Promise<ManualPlanDto> {
  const fd = new FormData()
  fd.append('file', file)

  const { data } = await axios.patch<ManualPlanDto>(`${MP_API_URL}/${id}/file`, fd, {
    headers: { 'Content-Type': 'multipart/form-data', Accept: 'application/json' },
    transformRequest: (d) => d,
    maxBodyLength: Infinity,
  })
  return data
}

/** 6) Soft delete */
export async function softDeleteManualPlan(id: number): Promise<void> {
  try {
    await axios.delete(`${MP_API_URL}/${id}`)
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Delete failed'))
  }
}

/** 7) Download / View URLs (use directly in href/src) */
export const manualPlanDownloadUrl = (id: number) => `${MP_API_URL}/${id}/download`
export const manualPlanViewUrl = (id: number) => `${MP_API_URL}/${id}/view`

/** 8) Adapter: map DTO -> UI row for your table */
export function mapDtoToRow(d: ManualPlanDto) {
  const fileName = d.name || `manual_${d.id}`
  const size = sizeToBytes(d.fileSize)
  return {
    id: d.id,
    name: d.name,
    dateOfApproval: d.dateOfApproval || undefined,
    revisionCount: Number(d.revisionCount ?? 0),       // 👈 number now
    approvedBy: d.approvedBy || undefined,

    uploadedByName: d.uploadedByName || '',
    uploadedBy: d.uploadedBy != null ? String(d.uploadedBy) : '',
    uploadedDate: d.uploadedDate || '',

    companyGroupAdminId: d.companyGroupId,
    companyAdminId: null,
    vesselId: d.vesselId || null,
    vesselName: d.vesselName || undefined,
    file: {
      name: fileName,
      url: manualPlanDownloadUrl(d.id),                // current/latest file
      type: d.fileMime || 'application/octet-stream',
      size,
    },
    remarks: d.remarks || undefined,
    sparePartIds: Array.isArray(d.sparePartIds) ? d.sparePartIds.map(Number).filter(Number.isFinite) : [],
  } as const
}

export async function listManualPlanSpareParts(companyGroupId: number): Promise<ManualPlanSparePartOption[]> {
  const { data } = await axios.get<ManualPlanSparePartOption[]>(
    `${MP_API_URL}/company-group/${companyGroupId}/spare-parts`
  )
  return Array.isArray(data) ? data : []
}

export async function listMappedManualPlansForPart(partId: number): Promise<ManualPlanDto[]> {
  const { data } = await axios.get<ManualPlanDto[]>(`${MP_API_URL}/part/${partId}`)
  return Array.isArray(data) ? data : []
}

export const manualPlanRevisionDownloadUrl = (revisionId: number) =>
  `${MP_API_URL}/revisions/${revisionId}/download`

export async function listManualPlanRevisions(id: number) {
  const { data } = await axios.get(`${MP_API_URL}/${id}/revisions`)
  return data as Array<{
    id: number
    revisionIndex: number
    filePath: string
    fileSize: string
    fileMime: string | null
    uploadedBy: number
    uploadedByName: string
    uploadedDate: string
    createdAt: string
  }>
}

export const manualPlanRevisionViewUrl = (revisionId: number) =>
  `${MP_API_URL}/revisions/${revisionId}/view`
// NOTE: If your backend path differs, mirror whatever you use for download
// (e.g., replace '/download' with '/view'). Keep auth headers same as others.

// ===============================
// CERTIFICATES (Vessel-scoped)
// ===============================

export const certificateViewUrl = (id: number) => `${CERT_API_URL}/view/${id}`
export const certificateDownloadUrl = (id: number) => `${CERT_API_URL}/download/${id}`

export const certificateRevisionViewUrl = (revisionId: number) =>
  `${CERT_API_URL}/revisions/${revisionId}/view`
export const certificateRevisionDownloadUrl = (revisionId: number) =>
  `${CERT_API_URL}/revisions/${revisionId}/download`

// ===============================
// CERTIFICATE CATEGORIES (Library)
// ===============================

const normalizeCompanyGroup = (c: any): CompanyGroupAdminLite => ({
  id: Number(c?.id),
  name: String(c?.name ?? c?.companyName ?? c?.companyGroupName ?? `Company ${c?.id ?? ''}`),
})

export async function listCompanyGroupAdmins(token: string): Promise<CompanyGroupAdminLite[]> {
  if (!token) return []
  const { data } = await axios.get(`${API_URL}/company-group-admins`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return (Array.isArray(data) ? data : []).map(normalizeCompanyGroup)
}

export async function listCertificateCategories(
  token: string,
  params?: { companyGroupId?: string | number }
): Promise<CertificateCategory[]> {
  if (!token) return []
  const { data } = await axios.get(CERT_CATEGORY_API_URL, {
    params: params?.companyGroupId ? { companyGroupId: params.companyGroupId } : undefined,
    headers: { Authorization: `Bearer ${token}` },
  })
  if (Array.isArray(data)) return data as CertificateCategory[]
  return (data?.content ?? []) as CertificateCategory[]
}

export async function listCertificateCategoryLibrary(
  token: string,
  params: {
    search?: string
    page?: number
    size?: number
    activeOnly?: boolean
    scope?: 'ALL' | 'GLOBAL' | 'COMPANY'
    companyGroupId?: string | number
    targetType?: string
  }
): Promise<{ content: CertificateCategory[]; totalElements: number }> {
  if (!token) return { content: [], totalElements: 0 }
  const { data } = await axios.get<PageResponse<CertificateCategory> | CertificateCategory[]>(
    CERT_CATEGORY_LIBRARY_API_URL,
    {
      params,
      headers: { Authorization: `Bearer ${token}` },
    }
  )

  const content = Array.isArray(data) ? data : data.content ?? []
  const totalElements = Array.isArray(data)
    ? data.length
    : data.totalElements ?? content.length

  return { content, totalElements }
}

export async function createCertificateCategory(
  token: string,
  payload: CertificateCategoryPayload
): Promise<CertificateCategory> {
  const { data } = await axios.post<CertificateCategory>(CERT_CATEGORY_API_URL, payload, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return data
}

export async function updateCertificateCategory(
  token: string,
  id: number,
  payload: CertificateCategoryPayload
): Promise<CertificateCategory> {
  const { data } = await axios.put<CertificateCategory>(`${CERT_CATEGORY_API_URL}/${id}`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return data
}

export async function deleteCertificateCategory(token: string, id: number): Promise<void> {
  await axios.delete(`${CERT_CATEGORY_API_URL}/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
}

// get one
export async function getCertificate(id: number) {
  const { data } = await axios.get(`${CERT_API_URL}/${id}`)
  return data as Certificate
}

// update meta
export async function updateCertificate(id: number, partial: Partial<Certificate>) {
  const { data } = await axios.put(`${CERT_API_URL}/${id}`, partial)
  return data as Certificate
}

// replace file (creates new revision)
export async function replaceCertificateFile(id: number, file: File) {
  const fd = new FormData()
  fd.append('file', file)
  const { data } = await axios.patch(`${CERT_API_URL}/${id}/file`, fd, {
    headers: { 'Content-Type': 'multipart/form-data', Accept: 'application/json' },
    transformRequest: (d) => d,
    maxBodyLength: Infinity,
  })
  return data as Certificate
}

// list revisions
export async function listCertificateRevisions(id: number) {
  const { data } = await axios.get(`${CERT_API_URL}/${id}/revisions`)
  return data as CertificateRevision[]
}

// ===============================
// OFFICE CERTIFICATES
// ===============================

export const officeCertificateViewUrl = (id: number) => `${OFFICE_CERT_API_URL}/view/${id}`
export const officeCertificateDownloadUrl = (id: number) => `${OFFICE_CERT_API_URL}/download/${id}`

export const officeCertificateRevisionViewUrl = (revisionId: number) =>
  `${OFFICE_CERT_API_URL}/revisions/${revisionId}/view`
export const officeCertificateRevisionDownloadUrl = (revisionId: number) =>
  `${OFFICE_CERT_API_URL}/revisions/${revisionId}/download`

export async function getOfficeCertificate(id: number) {
  const { data } = await axios.get(`${OFFICE_CERT_API_URL}/${id}`)
  return data as Certificate
}

export async function updateOfficeCertificate(id: number, partial: Partial<Certificate>) {
  const { data } = await axios.put(`${OFFICE_CERT_API_URL}/${id}`, partial)
  return data as Certificate
}

export async function replaceOfficeCertificateFile(id: number, file: File) {
  const fd = new FormData()
  fd.append('file', file)
  const { data } = await axios.patch(`${OFFICE_CERT_API_URL}/${id}/file`, fd, {
    headers: { 'Content-Type': 'multipart/form-data', Accept: 'application/json' },
    transformRequest: (d) => d,
    maxBodyLength: Infinity,
  })
  return data as Certificate
}

export async function listOfficeCertificateRevisions(id: number) {
  const { data } = await axios.get(`${OFFICE_CERT_API_URL}/${id}/revisions`)
  return data as CertificateRevision[]
}


// ===============================
// QHSE INSPECTION PLANS & INDEX
// ===============================

export type SearchInspectionsParams = {
  vesselId?: number
  /** Prefer this; we'll also accept kindId for backward compat */
  inspectionKindId?: number
  /** Back-compat alias (will be mapped to inspectionKindId) */
  kindId?: number
  fromDate?: string
  toDate?: string
  isFinalized?: boolean
  isVerified?: boolean
  companyGroupId?: number
  /** Subcompany/company (tenant) filter */
  companyId?: number
  /** Attachment/details; allow string to be lenient */
  inspectionType?: 'ATTACHMENT_TYPE' | 'DETAILS_TYPE' | string
  fromPortId?: number
  toPortId?: number
  /** Server-side filter for active ones only */
  activeOnly?: boolean
  page?: number
  size?: number
}

/** Optional server-side list endpoint already exists in your codebase */
export async function listInspectionPlans(params: {
  year?: number
  companyGroupId?: number
  vesselId?: number
  inspectionKindId?: number
}) {
  const res = await axios.get(`${QHSE_API}/inspection-plans`, { params })
  return res.data
}

// --- Unlinked Plans (client-side filter over listInspectionPlans) ---
export async function listUnlinkedPlans(params: {
  year?: number
  companyGroupId?: number
  vesselId?: number
  inspectionKindId?: number
}) {
  const res = await listInspectionPlans(params)
  const arr = Array.isArray(res) ? res : (Array.isArray(res?.content) ? res.content : [])
  return arr.filter((p: any) => !p?.inspectionId)
}

/**
 * Plans that match vessel + kind + month(forDate) and are NOT linked yet.
 * Server should return [{id, planDate, ...}] of unlinked plans only.
 */
export async function listAvailablePlans(params: {
  vesselId: number
  inspectionKindId: number
  forDate: string            // any date in the target month (YYYY-MM-DD)
}) {
  const { vesselId, inspectionKindId, forDate } = params
  const res = await axios.get(`${QHSE_API}/inspection-plans/available`, {
    params: { vesselId, inspectionKindId, forDate },
  })
  return res.data
}

/**
 * Candidate inspections for a given plan’s month/vessel/kind that are NOT linked yet
 * (Use this for "Link Existing Inspection" in the plan actions).
 */
// Shim: keep the same name used by UI, but route to an existing endpoint
export async function listUnlinkedInspections(params: {
  vesselId: number
  inspectionKindId: number
  forDate: string // any date in the target month (YYYY-MM-DD)
}) {
  const { vesselId, inspectionKindId, forDate } = params

  // Compute month [from, to] for server filtering
  const d = new Date(forDate)
  const yyyy = d.getFullYear()
  const mm = d.getMonth()
  const from = new Date(yyyy, mm, 1).toISOString().slice(0, 10)          // YYYY-MM-01
  const to = new Date(yyyy, mm + 1, 0).toISOString().slice(0, 10)        // YYYY-MM-last

  // Use the already-present "available" endpoint
  const { data } = await axios.get(`${INS_INDEX_API}/available`, {
    params: { vesselId, inspectionKindId, from, to },
  })

  // Normalize and ensure UNLINKED only (inspectionPlanId == null)
  const arr = Array.isArray(data) ? data : (Array.isArray(data?.content) ? data.content : [])
  return arr
    .filter((x: any) => x?.inspectionPlanId == null)
    .map((x: any) => ({
      ...x,
      internalInspectorName: x.internalInspectorName ?? x?.internalInspector?.fullName ?? null,
    }))
}

/** Link plan<->inspection (writes both sides: plan.inspectionId & inspection.inspectionPlanId) */
// match backend controller paths (path params, no JSON body)
export async function linkPlanToInspection(planId: number, inspectionId: number) {
  const res = await axios.post(
    `${QHSE_API}/inspection-plans/${planId}/attach-inspection/${inspectionId}`
  )
  return res.data
}

export async function unlinkPlan(planId: number) {
  const res = await axios.post(`${QHSE_API}/inspection-plans/${planId}/detach-inspection`)
  return res.data
}

export async function getPlanByInspection(inspectionId: number) {
  const res = await axios.get(`${QHSE_API}/inspection-plans/by-inspection/${inspectionId}`)
  return res.data as InspectionPlanDto | null
}

/** Get single plan (with inspectionId if linked) */
export async function getInspectionPlan(planId: number) {
  const res = await axios.get(`${QHSE_API}/inspection-plans/${planId}`)
  return res.data
}

export async function updatePlan(id: number, partial: Partial<InspectionPlanDto>) {
  const { data } = await axios.put(`${INS_PLAN_API}/${id}`, partial)
  return data as InspectionPlanDto
}

export async function deletePlan(id: number) {
  await axios.delete(`${INS_PLAN_API}/${id}`)
}

// BULK create plans
export async function bulkCreatePlans(body: CreatePlansPayload): Promise<InspectionPlanDto[]> {
  try {
    const { data } = await axios.post<InspectionPlanDto[]>(`${INS_PLAN_API}/bulk`, body)
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Bulk create failed'))
  }
}

// Available inspections (unassigned) for vessel+kind (+ optional range)
export async function listAvailableInspections(params: {
  vesselId: number
  inspectionKindId: number
  from?: string
  to?: string
}) {
  try {
    const { data } = await axios.get(`${INS_INDEX_API}/available`, { params })
    return data as Array<InspectionDto> | any[] // keep lenient if backend returns a lightweight shape
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Failed to list available inspections'))
  }
}

// --- index (inspections) ---
export async function searchInspections(params: SearchInspectionsParams) {
  const qp = { ...params, inspectionKindId: params.inspectionKindId ?? params.kindId }
  const { data } = await axios.get(INS_INDEX_API, { params: qp })
  return data // server may return array or pageable; component handles both
}

export async function createInspection(payload: CreateInspectionPayload): Promise<InspectionDto> {
  const { data } = await axios.post(INS_INDEX_API, payload)
  return data
}

export async function getInspection(id: number): Promise<InspectionDto> {
  const { data } = await axios.get(`${INS_INDEX_API}/${id}`)
  return data
}

export async function updateInspection(
  id: number,
  partial: Partial<InspectionDto>
): Promise<InspectionDto> {
  const { data } = await axios.put(`${INS_INDEX_API}/${id}`, partial)
  return data
}

export async function deleteInspection(id: number) {
  await axios.delete(`${INS_INDEX_API}/${id}`)
}

// --- mappers -> UI table rows ---
export function mapInspectionToRow(d: InspectionDto): InspectionRecord {
  return {
    id: d.id,
    vesselManager: '-',                                 // fill if you return it
    vessel: d.vesselName,
    vesselType: d.vesselType || '-',
    inspection: d.inspectionKindName,
    inspectionType: d.inspectionType?.replace('_', ' ') || '-',
    inspectionDate: d.inspectionFromDate,
    internalInspector: d.internalInspectorName || undefined,
    externalInspector: d.externalInspectorName || undefined,
    fromPort: d.fromPortName || undefined,
    toPort: d.toPortName || undefined,
    hoursOnboard: d.hoursOnboard ?? undefined,
    isFinalized: d.isFinalized ? 'Yes' : 'No',
    isVerified: d.isVerified ? 'Yes' : 'No',
    countNCRsObservations: d.countNcrsAndObservations ?? 0,
    countNCRsObservationsCompleted: d.countNcrsAndObservationsCompleted ?? 0,
    score: d.score ?? undefined,
    linkToPlan: d.id ? `/qhse/insplanning?inspectionId=${d.id}` : undefined,
    inspectionRemarks: d.remarks || undefined,
  }
}

// ===============================
// QHSE AUDIT INDEX (mirror inspections)
// ===============================

export type SearchAuditsParams = {
  vesselId?: number
  /** Prefer this; we'll also accept kindId for backward compat */
  auditKindId?: number
  /** Back-compat alias (will be mapped to inspectionKindId) */
  kindId?: number
  fromDate?: string
  toDate?: string
  isFinalized?: boolean
  isVerified?: boolean
  companyGroupId?: number
  /** Subcompany/company (tenant) filter */
  companyId?: number
  /** Attachment/details; allow string to be lenient */
  auditType?: 'ATTACHMENT_TYPE' | 'DETAILS_TYPE' | string
  fromPortId?: number
  toPortId?: number
  /** Server-side filter for active ones only */
  activeOnly?: boolean
  page?: number
  size?: number
}

export async function searchAudits(params: SearchAuditsParams) {
  const qp = { ...params, inspectionKindId: params.auditKindId ?? (params as any).kindId }
  const { data } = await axios.get(AUD_INDEX_API, { params: qp })
  // server may return array or pageable; component handles both
  return data
}

export async function createAudit(
  payload: CreateAuditPayload
): Promise<AuditDto> {
  const { data } = await axios.post(AUD_INDEX_API, payload)
  return data
}

export async function getAudit(id: number): Promise<AuditDto> {
  const { data } = await axios.get(`${AUD_INDEX_API}/${id}`)
  return data
}

export async function updateAudit(
  id: number,
  partial: Partial<AuditDto>
): Promise<AuditDto> {
  const { data } = await axios.put(`${AUD_INDEX_API}/${id}`, partial)
  return data
}

export async function deleteAudit(id: number) {
  await axios.delete(`${AUD_INDEX_API}/${id}`)
}

export async function uploadAuditAttachments(
  id: number,
  files: File[]
): Promise<AuditAttachmentInfo[]> {
  const form = new FormData()
  files.forEach((file) => form.append('files', file))
  const { data } = await axios.post<AuditAttachmentInfo[]>(
    `${AUD_INDEX_API}/${id}/attachments`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  )
  return Array.isArray(data) ? data : []
}

export async function listAuditAttachments(id: number): Promise<AuditAttachmentInfo[]> {
  const { data } = await axios.get<AuditAttachmentInfo[]>(`${AUD_INDEX_API}/${id}/attachments`)
  return Array.isArray(data) ? data : []
}

export const getAuthToken = (): string | undefined => {
  try {
    const raw = localStorage.getItem('kt-auth-react-v')
    if (!raw) return undefined
    const parsed = JSON.parse(raw)
    return parsed?.auth?.jwt || undefined
  } catch {
    return undefined
  }
}

export const auditAttachmentViewUrl = (auditId: number, attachmentId: number) => {
  const token = getAuthToken()
  const base = `${AUD_INDEX_API}/${auditId}/attachments/${attachmentId}/view`
  return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

export const auditAttachmentDownloadUrl = (auditId: number, attachmentId: number) => {
  const token = getAuthToken()
  const base = `${AUD_INDEX_API}/${auditId}/attachments/${attachmentId}/download`
  return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

export async function deleteAuditAttachment(auditId: number, attachmentId: number): Promise<void> {
  await axios.delete(`${AUD_INDEX_API}/${auditId}/attachments/${attachmentId}`)
}

export async function uploadInspectionAttachments(
  id: number,
  files: File[]
): Promise<InspectionAttachmentInfo[]> {
  const form = new FormData()
  files.forEach((file) => form.append('files', file))
  const { data } = await axios.post<InspectionAttachmentInfo[]>(
    `${INS_INDEX_API}/${id}/attachments`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  )
  return Array.isArray(data) ? data : []
}

export async function listInspectionAttachments(id: number): Promise<InspectionAttachmentInfo[]> {
  const { data } = await axios.get<InspectionAttachmentInfo[]>(`${INS_INDEX_API}/${id}/attachments`)
  return Array.isArray(data) ? data : []
}

export const inspectionAttachmentViewUrl = (inspectionId: number, attachmentId: number) => {
  const token = getAuthToken()
  const base = `${INS_INDEX_API}/${inspectionId}/attachments/${attachmentId}/view`
  return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

export const inspectionAttachmentDownloadUrl = (inspectionId: number, attachmentId: number) => {
  const token = getAuthToken()
  const base = `${INS_INDEX_API}/${inspectionId}/attachments/${attachmentId}/download`
  return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

export async function deleteInspectionAttachment(inspectionId: number, attachmentId: number): Promise<void> {
  await axios.delete(`${INS_INDEX_API}/${inspectionId}/attachments/${attachmentId}`)
}

/** Map Audit DTO -> UI row (shares same shape as InspectionRecord) */
export function mapAuditToRow(d: AuditDto): AuditRecord {
  return {
    id: d.id,
    vesselManager: '-',
    vessel: d.vesselName,
    vesselType: d.vesselType || '-',
    audit: d.auditKindName,
    auditType: d.auditType?.replace('_', ' ') || '-',
    auditDate: d.auditFromDate,
    // show internalAuditorName in the "Internal Inspector" column
    internalAuditor: d.internalAuditorName || undefined,
    // show externalAuditorName in the "External Inspector" column
    externalAuditor: d.externalAuditorName || undefined,
    fromPort: d.fromPortName || undefined,
    toPort: d.toPortName || undefined,
    hoursOnboard: d.hoursOnboard ?? undefined,
    isFinalized: d.isFinalized ? 'Yes' : 'No',
    isVerified: d.isVerified ? 'Yes' : 'No',
    countNCRsObservations: d.countNcrsAndObservations ?? 0,
    countNCRsObservationsCompleted: d.countNcrsAndObservationsCompleted ?? 0,
    score: d.score ?? undefined,
    linkToPlan: d.id ? `/qhse/audit-planning?auditId=${d.id}` : undefined,
    auditRemarks: d.remarks || undefined,
  }
}

/** Available audits for vessel+kind (+ optional range) */
export async function listAvailableAudits(params: {
  vesselId: number
  auditKindId: number
  from?: string
  to?: string
}) {
  try {
    const { data } = await axios.get(`${AUD_INDEX_API}/available`, { params })
    return data as AuditDto[] | any[]
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Failed to list available audits'))
  }
}

/**
 * Candidate audits for a given plan’s month/vessel/kind that are NOT linked yet.
 * This is used by AuditPlanActionsModal to show "Link Existing Audit".
 */
export async function listUnlinkedAudits(params: {
  vesselId: number
  auditKindId: number
  forDate: string // any date in the target month (YYYY-MM-DD)
}) {
  const { vesselId, auditKindId, forDate } = params

  const d = new Date(forDate)
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Invalid forDate "${forDate}" passed to listUnlinkedAudits`)
  }

  const yyyy = d.getFullYear()
  const mm = d.getMonth()
  const from = new Date(yyyy, mm, 1).toISOString().slice(0, 10)      // YYYY-MM-01
  const to = new Date(yyyy, mm + 1, 0).toISOString().slice(0, 10)    // YYYY-MM-last

  // Pure audit endpoint – no inspection URLs here
  const { data } = await axios.get(`${AUD_INDEX_API}/available`, {
    params: { vesselId, auditKindId, from, to },
  })

  const arr = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.content)
      ? (data as any).content
      : []

  return arr
    // support both auditPlanId and legacy inspectionPlanId, but we don't *show* that anywhere
    .filter((x: any) => x?.auditPlanId == null && x?.auditPlanId == null)
    .map((x: any) => ({
      ...x,
      // normalize to new free-text audit field
      internalAuditorName:
        x.internalAuditorName ??
        x.internalAuditorName ??
        x?.internalAuditor?.fullName ??
        null,
    }))
}

// ===============================
// QHSE AUDIT PLANS (mirror inspection plans)
// ===============================

export async function listAuditPlans(params: {
  year?: number
  companyGroupId?: number
  vesselId?: number
  auditKindId?: number
}) {
  const res = await axios.get(`${AUD_PLAN_API}`, { params })
  return res.data as AuditPlanDto[] | any[]
}

export async function bulkCreateAuditPlans(
  body: CreateAuditPlansPayload
): Promise<AuditPlanDto[]> {
  try {
    const { data } = await axios.post<AuditPlanDto[]>(`${AUD_PLAN_API}/bulk`, body)
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Bulk create audit plans failed'))
  }
}

/** Plans that match vessel + kind + month(forDate) and are NOT linked yet. */
export async function listAvailableAuditPlans(params: {
  vesselId: number
  auditKindId: number
  forDate: string // YYYY-MM-DD in target month
}) {
  const { vesselId, auditKindId, forDate } = params
  const res = await axios.get(`${AUD_PLAN_API}/available`, {
    params: { vesselId, auditKindId, forDate },
  })
  return res.data
}

/** Link auditPlan <-> audit */
export async function linkAuditPlanToAudit(planId: number, auditId: number) {
  const res = await axios.post(
    `${AUD_PLAN_API}/${planId}/attach-audit/${auditId}`
  )
  return res.data
}

/** Unlink auditPlan from audit */
export async function unlinkAuditPlan(planId: number) {
  const res = await axios.post(`${AUD_PLAN_API}/${planId}/detach-audit`)
  return res.data
}

/** Get plan mapped by audit id (reverse lookup) */
export async function getAuditPlanByAudit(auditId: number) {
  const res = await axios.get(`${AUD_PLAN_API}/by-audit/${auditId}`)
  return res.data as AuditPlanDto | null
}

/** Get single Audit plan */
export async function getAuditPlan(planId: number) {
  const res = await axios.get(`${AUD_PLAN_API}/${planId}`)
  return res.data as AuditPlanDto
}

export async function updateAuditPlan(
  id: number,
  partial: Partial<AuditPlanDto>
) {
  const { data } = await axios.put(`${AUD_PLAN_API}/${id}`, partial)
  return data as AuditPlanDto
}

export async function deleteAuditPlan(id: number) {
  await axios.delete(`${AUD_PLAN_API}/${id}`)
}

// ===============================
// QHSE DEFECT APIs
// ===============================

export type SearchDefectsParams = {
  companyGroupId?: number
  companyId?: number
  vesselId?: number
  category?: DefectCategory
  dacCode?: number
  fromDate?: string   // yyyy-MM-dd
  toDate?: string     // yyyy-MM-dd
  activeOnly?: boolean
}

export async function searchRequisitionLinkOptions(params?: {
  q?: string
  vesselId?: number
  limit?: number
}): Promise<RequisitionLinkOption[]> {
  const { data } = await axios.get<RequisitionLinkOption[]>(REQUISITION_LINK_OPTIONS_URL, { params })
  return Array.isArray(data) ? data : []
}

const categoryLabel = (c?: DefectCategory | null) => {
  switch (c) {
    case 'THIRD_PARTY_INSPECTION': return 'Third party inspection'
    case 'SHIP_OBSERVATION': return 'Ship observation'
    case 'OFFICE_INSPECTION': return 'Office inspection'
    default: return '-'
  }
}

const tpiSubCategoryLabel = (t?: TpiSubCategory | null) => {
  switch (t) {
    case 'EXT_AUDIT': return 'Ext. Audit (external Audit)'
    case 'SIRE': return 'SIRE'
    case 'PSC_INSPECTION': return 'PSC Inspection (port state control)'
    case 'FSI_INSPECTION': return 'FSI Inspection (Flag State Inspection)'
    case 'OTHER': return 'Others'
    default: return undefined
  }
}

const smsCodeLabel = (s?: SmsCode | null) => {
  switch (s) {
    case 'SMS_OTHER_30_DAYS': return 'Others 1: close in 30 days'
    case 'SMS_OTHER_45_DAYS': return 'Others 2: close in 45 days'
    case 'SMS_OTHER_90_DAYS': return 'Others 3: close in 90 days'
    default: return undefined
  }
}

/** NEW: Department label */
const departmentLabel = (d?: DefectDepartment | null) => {
  switch (d) {
    case 'DECK': return 'Deck'
    case 'MACHINERY': return 'Machinery'
    case 'LSA_FFA_SAFETY': return 'LSA/FFA (safety)'
    case 'NAVIGATION': return 'Navigation'
    case 'MLC_OCCUPATIONAL_HEALTH': return 'MLC (occupational health)'
    case 'EMS_ENVIRONMENT_MANAGEMENT_SYSTEM': return 'EMS (environment management system)'
    case 'ENGINE': return 'Engine'
    case 'FSM_FOOD_SAFETY_MANAGEMENT': return 'FSM (food safety management)'
    case 'CERTIFICATION_AND_DOCUMENTATION': return 'Certification and documentation'
    case 'OTHERS': return 'Others'
    default: return undefined
  }
}

/** NEW: Status label */
const statusLabel = (s?: DefectStatus | null) => {
  switch (s) {
    case 'SAVE': return 'Saved'
    case 'SUBMIT': return 'Submitted'
    case 'PENDING_APPROVAL': return 'Pending Approval'
    case 'APPROVED': return 'Approved'
    case 'REJECTED': return 'Rejected'
    case 'CLOSE': return 'Closed'
    case 'REVIEWED': return 'Reviewed'
    default: return '-'
  }
}

const closureResultStatusLabel = (
  status?: DefectStatus | null,
  closureResult?: DefectClosureResult | null
) => {
  if (status !== 'CLOSE' && status !== 'REVIEWED') return undefined

  switch (closureResult) {
    case 'TO_BE_REVIEWED_NEXT_INSPECTION':
      return 'Closed and TBR'
    case 'SATISFACTORY':
    default:
      return 'Closed Satisfactorily'
  }
}

/** Raw list from server */
export async function searchDefects(params: SearchDefectsParams): Promise<QHSEDefectDto[]> {
  const { data } = await axios.get<QHSEDefectDto[]>(DEFECT_API_URL, { params })
  return Array.isArray(data) ? data : []
}

/** Map DTO -> UI row for table */
export function mapDefectToRow(
  d: QHSEDefectDto,
  vesselMeta?: { companyGroupAdminId?: number | null; companyAdminId?: number | null }
): DefectRecord {
  const status: DefectStatus | undefined = (d.status as DefectStatus | undefined) ?? 'SAVE'

  // --- NEW: normalize inspection/audit link ---
  const isInspection = d.inspectionId != null && Number(d.inspectionId) > 0
  const isAudit = d.auditId != null && Number(d.auditId) > 0

  let linkedType: 'INSPECTION' | 'AUDIT' | null = null
  let linkedLabel: string | null = null

  if (isInspection && !isAudit) {
    linkedType = 'INSPECTION'
    linkedLabel =
      d.inspectionDisplayName ||
      d.displayTitle ||
      (d.dateDefect
        ? `Inspection #${d.inspectionId} (${d.dateDefect})`
        : `Inspection #${d.inspectionId}`)
  } else if (isAudit && !isInspection) {
    linkedType = 'AUDIT'
    linkedLabel =
      d.auditDisplayName ||
      d.displayTitle ||
      (d.dateDefect
        ? `Audit #${d.auditId} (${d.dateDefect})`
        : `Audit #${d.auditId}`)
  }

  return {
    id: Number(d.id),
    vesselId: Number(d.vesselId),
    vesselName: d.vesselName || '-',

    defectNumber: d.defectNumber ?? null,

    inspectionId: d.inspectionId ?? null,
    auditId: d.auditId ?? null,
    linkedType,
    linkedLabel,

    category: d.category,
    categoryLabel: categoryLabel(d.category),

    tpiSubCategory: d.tpiSubCategory ?? null,
    tpiSubCategoryLabel: tpiSubCategoryLabel(d.tpiSubCategory),

    dacCode: d.dacCode,
    dacActionName: d.dacActionName ?? null,

    smsCode: d.smsCode ?? null,
    smsCodeLabel: smsCodeLabel(d.smsCode),

    description: d.description ?? null,
    dateObserved: d.dateObserved ?? null,
    dateDefect: d.dateDefect ?? null,

    requisitionId: d.requisitionId ?? null,
    applicableRequisitionNumber: d.applicableRequisitionNumber ?? null,

    department: d.department ?? null,
    departmentLabel: departmentLabel(d.department),

    attachmentPath: d.attachmentPath ?? null,
    closureEvidencePath: d.closureEvidencePath ?? null,

    correctiveAction: d.correctiveAction ?? null,
    preventiveAction: d.preventiveAction ?? null,

    remarks: d.remarks ?? null,
    shoreRemarks: d.shoreRemarks ?? null,
    rejectionRemark: d.rejectionRemark ?? null,
    approvedAt: d.approvedAt ?? null,
    approvedByName: d.approvedByName ?? null,
    closureRemark: d.closureRemark ?? null,
    officeRemarks: d.officeRemarks ?? null,
    officeReviewedAt: d.officeReviewedAt ?? null,
    officeReviewedByName: d.officeReviewedByName ?? null,

    status,
    statusLabel: statusLabel(status),

    closureResult: d.closureResult ?? null,
    closureResultLabel: closureResultStatusLabel(status, d.closureResult ?? null),

    displayTitle: d.displayTitle ?? null,

    equipmentId: d.equipmentId ?? null,
    equipmentName: d.equipmentName ?? null,
    componentId: d.componentId ?? null,
    componentName: d.componentName ?? null,
    subComponentId: d.subComponentId ?? null,
    subComponentName: d.subComponentName ?? null,
    subComponentIds: d.subComponentIds ?? null,
    subComponentNames: d.subComponentNames ?? null,
    jobPlanLineId: d.jobPlanLineId ?? null,
    jobPlanLineJobCode: d.jobPlanLineJobCode ?? null,
    jobPlanLineTaskDescription: d.jobPlanLineTaskDescription ?? null,
    jobPlanLineScheduleType: d.jobPlanLineScheduleType ?? null,
    jobPlanLineIds: d.jobPlanLineIds ?? null,
    jobPlanLineJobCodes: d.jobPlanLineJobCodes ?? null,
    jobPlanLineTaskDescriptions: d.jobPlanLineTaskDescriptions ?? null,
    jobPlanLineScheduleTypes: d.jobPlanLineScheduleTypes ?? null,
    jobPlanLineSubComponentIds: d.jobPlanLineSubComponentIds ?? null,
    jobPlanLineSubComponentNames: d.jobPlanLineSubComponentNames ?? null,

    companyGroupAdminId: vesselMeta?.companyGroupAdminId ?? null,
    companyAdminId: vesselMeta?.companyAdminId ?? null,
  }
}

/** Create defect (JSON, no files yet) */
export async function createDefect(
  payload: Omit<QHSEDefectDto,
    'id' | 'createdAt' | 'updatedAt' | 'vesselName' | 'displayTitle' | 'defectNumber' | 'status'>
): Promise<QHSEDefectDto> {
  const { data } = await axios.post<QHSEDefectDto>(DEFECT_API_URL, payload)
  return data
}

/** Get one defect */
export async function getDefect(id: number): Promise<QHSEDefectDto> {
  const { data } = await axios.get<QHSEDefectDto>(`${DEFECT_API_URL}/${id}`)
  return data
}

/** Update defect */
export async function updateDefect(
  id: number,
  partial: Partial<Omit<QHSEDefectDto,
    'id' | 'createdAt' | 'updatedAt' | 'vesselName' | 'defectNumber' | 'status'>>
): Promise<QHSEDefectDto> {
  const { data } = await axios.put<QHSEDefectDto>(`${DEFECT_API_URL}/${id}`, partial)
  return data
}

/** Submit (SAVE/REJECTED -> PENDING_APPROVAL) */
export async function submitDefect(id: number): Promise<QHSEDefectDto> {
  const { data } = await axios.post<QHSEDefectDto>(`${DEFECT_API_URL}/${id}/submit`)
  return data
}

export async function reviewDefectByOffice(id: number, officeRemarks: string): Promise<QHSEDefectDto> {
  const {data} = await axios.post<QHSEDefectDto>(`${DEFECT_API_URL}/${id}/office-review`, {officeRemarks})
  return data
}

/** Approve a defect (PENDING_APPROVAL -> APPROVED) – shore team */
export async function approveDefect(
  id: number,
  shoreRemarks?: string
): Promise<QHSEDefectDto> {
  const { data } = await axios.post<QHSEDefectDto>(
    `${DEFECT_API_URL}/${id}/approve`,
    null,
    { params: shoreRemarks ? { shoreRemarks } : undefined }
  )
  return data
}

/** Reject a defect (PENDING_APPROVAL -> REJECTED) – shore team */
export async function rejectDefect(
  id: number,
  rejectionRemark?: string,
  shoreRemarks?: string
): Promise<QHSEDefectDto> {
  const { data } = await axios.post<QHSEDefectDto>(
    `${DEFECT_API_URL}/${id}/reject`,
    null,
    {
      params: {
        ...(rejectionRemark ? { rejectionRemark } : {}),
        ...(shoreRemarks ? { shoreRemarks } : {}),
      },
    }
  )
  return data
}

/** Close (APPROVED -> CLOSE) with closure result */
export async function closeDefect(
  id: number,
  result?: DefectClosureResult
): Promise<QHSEDefectDto> {
  const { data } = await axios.post<QHSEDefectDto>(
    `${DEFECT_API_URL}/${id}/close`,
    null,
    {
      params: result ? { result } : undefined,
    }
  )
  return data
}

/** Hard delete (if you later add button) */
export async function deleteDefect(id: number): Promise<void> {
  await axios.delete(`${DEFECT_API_URL}/${id}`)
}

/** Soft delete wrapper if you use /{id}/soft-delete */
export async function softDeleteDefect(id: number): Promise<void> {
  await axios.post(`${DEFECT_API_URL}/${id}/soft-delete`)
}

/** Upload / replace main attachment document (multi-file) */
export async function uploadDefectAttachment(
  id: number,
  files: File | File[]
): Promise<QHSEDefectDto> {
  const fd = new FormData()
  const arr = Array.isArray(files) ? files : [files]
  arr.forEach((file) => {
    fd.append('files', file)
  })

  const { data } = await axios.post<QHSEDefectDto>(
    `${DEFECT_API_URL}/${id}/attachment`,
    fd,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      transformRequest: (d) => d,
      maxBodyLength: Infinity,
    }
  )
  return data
}

/** Upload / replace closure evidence document */
export async function uploadDefectClosureEvidence(
  id: number,
  file: File
): Promise<QHSEDefectDto> {
  const fd = new FormData()
  fd.append('file', file)

  const { data } = await axios.post<QHSEDefectDto>(
    `${DEFECT_API_URL}/${id}/closure-evidence`,
    fd,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )
  return data
}

// ---- Defect File View / Download URLs ----

export const defectAttachmentViewUrl = (id: number) =>
  `${DEFECT_API_URL}/${id}/attachment/view`

export const defectAttachmentDownloadUrl = (id: number) =>
  `${DEFECT_API_URL}/${id}/attachment/download`

export const defectClosureViewUrl = (id: number) =>
  `${DEFECT_API_URL}/${id}/closure-evidence/view`

export const defectClosureDownloadUrl = (id: number) =>
  `${DEFECT_API_URL}/${id}/closure-evidence/download`

/** Delete closure evidence file */
export async function deleteDefectClosureEvidence(id: number): Promise<void> {
  await axios.delete(`${DEFECT_API_URL}/${id}/closure-evidence`)
}

// Multi-attachment helpers

export const defectAttachmentFileViewUrl = (id: number, fileName: string) =>
  `${DEFECT_API_URL}/${id}/attachment/view?file=${encodeURIComponent(fileName)}`

export const defectAttachmentFileDownloadUrl = (id: number, fileName: string) =>
  `${DEFECT_API_URL}/${id}/attachment/download?file=${encodeURIComponent(fileName)}`

export type DefectAttachmentInfo = {
  fileName: string
  webPath: string
  sizeBytes: number
  mimeType?: string | null
}

/** List all attachment files of a defect */
export async function listDefectAttachments(
  id: number
): Promise<DefectAttachmentInfo[]> {
  const { data } = await axios.get(`${DEFECT_API_URL}/${id}/attachments`)
  return Array.isArray(data) ? data : []
}

/** Delete a specific attachment by fileName */
export async function deleteDefectAttachment(
  id: number,
  fileName: string
): Promise<void> {
  await axios.delete(`${DEFECT_API_URL}/${id}/attachment`, {
    params: { file: fileName },
  })
}

// =====================
// QHSE – Inspection Findings
// =====================

export type SearchInspectionFindingParams = {
  companyGroupId?: number
  companyId?: number
  vesselId?: number
  inspectionId?: number
  status?: FindingStatus
  fromDate?: string
  toDate?: string
  activeOnly?: boolean
}

/**
 * Search / list inspection findings
 */
export async function searchInspectionFindings(
  params: SearchInspectionFindingParams
): Promise<QHSEInspectionFindingDto[]> {
  const { data } = await axios.get<QHSEInspectionFindingDto[]>(INSPECTION_FINDING_API_URL, {
    params,
  })
  return data
}

/**
 * Get single finding
 */
export async function getInspectionFinding(id: number): Promise<QHSEInspectionFindingDto> {
  const { data } = await axios.get<QHSEInspectionFindingDto>(
    `${INSPECTION_FINDING_API_URL}/${id}`
  )
  return data
}

/**
 * Create finding – you MUST at least send: inspectionId, findingName, status.
 */
export async function createInspectionFinding(
  payload: Partial<QHSEInspectionFindingDto> & {
    inspectionId: number
    findingName: string
    status: FindingStatus
  }
): Promise<QHSEInspectionFindingDto> {
  const { data } = await axios.post<QHSEInspectionFindingDto>(
    INSPECTION_FINDING_API_URL,
    payload
  )
  return data
}

/**
 * Update finding
 */
export async function updateInspectionFinding(
  id: number,
  payload: Partial<QHSEInspectionFindingDto>
): Promise<QHSEInspectionFindingDto> {
  const { data } = await axios.put<QHSEInspectionFindingDto>(
    `${INSPECTION_FINDING_API_URL}/${id}`,
    payload
  )
  return data
}

/**
 * Hard delete
 */
export async function deleteInspectionFinding(id: number): Promise<void> {
  await axios.delete(`${INSPECTION_FINDING_API_URL}/${id}`)
}

/**
 * Soft delete (mark inactive)
 */
export async function softDeleteInspectionFinding(id: number): Promise<void> {
  await axios.post(`${INSPECTION_FINDING_API_URL}/${id}/soft-delete`)
}

/**
 * Close (mark completed) — updates the parent inspection's completed count
 */
export async function closeInspectionFinding(id: number): Promise<QHSEInspectionFindingDto> {
  const { data } = await axios.post<QHSEInspectionFindingDto>(
    `${INSPECTION_FINDING_API_URL}/${id}/close`
  )
  return data
}

/**
 * Reopen a previously closed finding
 */
export async function reopenInspectionFinding(id: number): Promise<QHSEInspectionFindingDto> {
  const { data } = await axios.post<QHSEInspectionFindingDto>(
    `${INSPECTION_FINDING_API_URL}/${id}/reopen`
  )
  return data
}

/** Submit finding for shore approval (DRAFT/REJECTED -> PENDING_APPROVAL) */
export async function submitInspectionFinding(id: number): Promise<QHSEInspectionFindingDto> {
  const { data } = await axios.post<QHSEInspectionFindingDto>(
    `${INSPECTION_FINDING_API_URL}/${id}/submit`
  )
  return data
}

/** Approve inspection finding – shore team */
export async function approveInspectionFinding(
  id: number,
  shoreRemarks?: string
): Promise<QHSEInspectionFindingDto> {
  const { data } = await axios.post<QHSEInspectionFindingDto>(
    `${INSPECTION_FINDING_API_URL}/${id}/approve`,
    null,
    { params: shoreRemarks ? { shoreRemarks } : undefined }
  )
  return data
}

/** Reject inspection finding – shore team */
export async function rejectInspectionFinding(
  id: number,
  rejectionRemark?: string,
  shoreRemarks?: string
): Promise<QHSEInspectionFindingDto> {
  const { data } = await axios.post<QHSEInspectionFindingDto>(
    `${INSPECTION_FINDING_API_URL}/${id}/reject`,
    null,
    {
      params: {
        ...(rejectionRemark ? { rejectionRemark } : {}),
        ...(shoreRemarks ? { shoreRemarks } : {}),
      },
    }
  )
  return data
}

// ---------------------
// Attachments
// ---------------------

export async function uploadInspectionFindingAttachments(
  id: number,
  files: File[]
): Promise<QHSEInspectionFindingDto> {
  const form = new FormData()
  files.forEach((f) => form.append('files', f))

  const { data } = await axios.post<QHSEInspectionFindingDto>(
    `${INSPECTION_FINDING_API_URL}/${id}/attachments`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  )
  return data
}

export async function listInspectionFindingAttachments(
  id: number
): Promise<InspectionFindingAttachmentInfo[]> {
  const { data } = await axios.get<InspectionFindingAttachmentInfo[]>(
    `${INSPECTION_FINDING_API_URL}/${id}/attachments`
  )
  return data
}

// View / download single "main" attachment
export const inspectionFindingAttachmentViewUrl = (id: number) =>
  `${INSPECTION_FINDING_API_URL}/${id}/attachment/view`

export const inspectionFindingAttachmentDownloadUrl = (id: number) =>
  `${INSPECTION_FINDING_API_URL}/${id}/attachment/download`

// View / download a specific file by fileName
export const inspectionFindingAttachmentFileViewUrl = (id: number, fileName: string) =>
  `${INSPECTION_FINDING_API_URL}/${id}/attachment/view?file=${encodeURIComponent(fileName)}`

export const inspectionFindingAttachmentFileDownloadUrl = (id: number, fileName: string) =>
  `${INSPECTION_FINDING_API_URL}/${id}/attachment/download?file=${encodeURIComponent(fileName)}`

/** Delete a specific inspection finding attachment by fileName */
export async function deleteInspectionFindingAttachment(
  id: number,
  fileName: string
): Promise<void> {
  await axios.delete(`${INSPECTION_FINDING_API_URL}/${id}/attachment`, {
    params: { file: fileName },
  })
}

// =====================
// QHSE – Audit Findings
// =====================

export type SearchAuditFindingParams = {
  companyGroupId?: number
  companyId?: number
  vesselId?: number
  auditId?: number
  status?: FindingStatus
  fromDate?: string
  toDate?: string
  activeOnly?: boolean
}

/**
 * Search / list inspection findings
 */
export async function searchAuditFindings(
  params: SearchAuditFindingParams
): Promise<QHSEAuditFindingDto[]> {
  const { data } = await axios.get<QHSEAuditFindingDto[]>(AUDIT_FINDING_API_URL, {
    params,
  })
  return data
}

/**
 * Get single finding
 */
export async function getAuditFinding(id: number): Promise<QHSEAuditFindingDto> {
  const { data } = await axios.get<QHSEAuditFindingDto>(`${AUDIT_FINDING_API_URL}/${id}`)
  return data
}

/**
 * Create finding – you MUST at least send: inspectionId, findingName, status.
 */
export async function createAuditFinding(
  payload: Partial<QHSEAuditFindingDto> & {
    auditId: number
    findingName: string
    status: FindingStatus
  }
): Promise<QHSEAuditFindingDto> {
  const { data } = await axios.post<QHSEAuditFindingDto>(
    AUDIT_FINDING_API_URL,
    payload
  )
  return data
}

/**
 * Update finding
 */
export async function updateAuditFinding(
  id: number,
  payload: Partial<QHSEAuditFindingDto>
): Promise<QHSEAuditFindingDto> {
  const { data } = await axios.put<QHSEAuditFindingDto>(
    `${AUDIT_FINDING_API_URL}/${id}`,
    payload
  )
  return data
}

/**
 * Hard delete
 */
export async function deleteAuditFinding(id: number): Promise<void> {
  await axios.delete(`${AUDIT_FINDING_API_URL}/${id}`)
}

/**
 * Soft delete (mark inactive)
 */
export async function softDeleteAuditFinding(id: number): Promise<void> {
  await axios.post(`${AUDIT_FINDING_API_URL}/${id}/soft-delete`)
}

/**
 * Mark a finding as completed / closed (updates audit completed counts)
 */
export async function closeAuditFinding(id: number): Promise<QHSEAuditFindingDto> {
  const { data } = await axios.post<QHSEAuditFindingDto>(`${AUDIT_FINDING_API_URL}/${id}/close`)
  return data
}

/**
 * Reopen a previously closed finding
 */
export async function reopenAuditFinding(id: number): Promise<QHSEAuditFindingDto> {
  const { data } = await axios.post<QHSEAuditFindingDto>(`${AUDIT_FINDING_API_URL}/${id}/reopen`)
  return data
}

/** Submit audit finding for shore approval (DRAFT/REJECTED -> PENDING_APPROVAL) */
export async function submitAuditFinding(id: number): Promise<QHSEAuditFindingDto> {
  const { data } = await axios.post<QHSEAuditFindingDto>(
    `${AUDIT_FINDING_API_URL}/${id}/submit`
  )
  return data
}

/** Approve audit finding – shore team */
export async function approveAuditFinding(
  id: number,
  shoreRemarks?: string
): Promise<QHSEAuditFindingDto> {
  const { data } = await axios.post<QHSEAuditFindingDto>(
    `${AUDIT_FINDING_API_URL}/${id}/approve`,
    null,
    { params: shoreRemarks ? { shoreRemarks } : undefined }
  )
  return data
}

/** Reject audit finding – shore team */
export async function rejectAuditFinding(
  id: number,
  rejectionRemark?: string,
  shoreRemarks?: string
): Promise<QHSEAuditFindingDto> {
  const { data } = await axios.post<QHSEAuditFindingDto>(
    `${AUDIT_FINDING_API_URL}/${id}/reject`,
    null,
    {
      params: {
        ...(rejectionRemark ? { rejectionRemark } : {}),
        ...(shoreRemarks ? { shoreRemarks } : {}),
      },
    }
  )
  return data
}

export async function uploadAuditFindingAttachments(
  id: number,
  files: File[]
): Promise<QHSEAuditFindingDto> {
  const form = new FormData()
  files.forEach((f) => form.append('files', f))

  const { data } = await axios.post<QHSEAuditFindingDto>(
    `${AUDIT_FINDING_API_URL}/${id}/attachments`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  )
  return data
}

export async function listAuditFindingAttachments(
  id: number
): Promise<AuditFindingAttachmentInfo[]> {
  const { data } = await axios.get<AuditFindingAttachmentInfo[]>(
    `${AUDIT_FINDING_API_URL}/${id}/attachments`
  )
  return data
}

// View / download single "main" attachment
export const AuditFindingAttachmentViewUrl = (id: number) =>
  `${AUDIT_FINDING_API_URL}/${id}/attachment/view`

export const AuditFindingAttachmentDownloadUrl = (id: number) =>
  `${AUDIT_FINDING_API_URL}/${id}/attachment/download`

// View / download a specific file by fileName
export const AuditFindingAttachmentFileViewUrl = (id: number, fileName: string) =>
  `${AUDIT_FINDING_API_URL}/${id}/attachment/view?file=${encodeURIComponent(fileName)}`

export const AuditFindingAttachmentFileDownloadUrl = (id: number, fileName: string) =>
  `${AUDIT_FINDING_API_URL}/${id}/attachment/download?file=${encodeURIComponent(fileName)}`

/** Delete a specific inspection finding attachment by fileName */
export async function deleteAuditFindingAttachment(
  id: number,
  fileName: string
): Promise<void> {
  await axios.delete(`${AUDIT_FINDING_API_URL}/${id}/attachment`, {
    params: { file: fileName },
  })
}

// ===============================
// QHSE NEAR MISS REPORT APIs
// ===============================

export type SearchNearMissParams = {
  companyGroupId?: number
  companyId?: number
  vesselId?: number
  occurrenceType?: NearMissOccurrenceType
  status?: NearMissStatus
  fromDate?: string    // yyyy-MM-dd
  toDate?: string      // yyyy-MM-dd
  activeOnly?: boolean
}

const occurrenceTypeLabel = (t?: NearMissOccurrenceType | null) => {
  switch (t) {
    case 'ACCIDENT': return 'Accident'
    case 'INCIDENT': return 'Incident'
    case 'NEAR_MISS': return 'Near miss'
    case 'HIGH_SEVERITY_NEAR_MISS': return 'High severity near miss'
    default: return '-'
  }
}

const locationOfOccurrenceLabel = (locs?: NearMissLocationOfOccurrence[] | null) => {
  if (!locs || !locs.length) return '-'
  const mapOne = (l: NearMissLocationOfOccurrence) => {
    switch (l) {
      case 'ACCOMMODATION': return 'Accommodation'
      case 'BRIDGE': return 'Bridge'
      case 'ASHORE': return 'Ashore'
      case 'ENGINE_ROOM_MACHINERY_SPACES': return 'Engine Room / Machinery Spaces'
      case 'DECK': return 'Deck'
      case 'ALOFT_OVERBOARD': return 'Aloft / Overboard'
      case 'ENCLOSED_SPACES': return 'Enclosed Spaces'
      case 'ENCLOSED_SPACES_MACHINERY': return 'Enclosed Spaces (Machinery)'
      case 'OFFICE': return 'Office'
      case 'STORE': return 'Store'
      case 'TRAFFIC_ROAD': return 'Traffic Road'
      case 'OTHER': return 'Other'
      default: return l
    }
  }
  return locs.map(mapOne).join(', ')
}

const substandardActsLabel = (acts?: NearMissSubstandardAct[] | null) => {
  if (!acts || !acts.length) return '-'
  const mapOne = (a: NearMissSubstandardAct) => {
    switch (a) {
      case 'FAILURE_TO_FOLLOW_RULES': return 'Failure To Follow Rules'
      case 'FAILURE_TO_USE_PPE_PROPERLY': return 'Failure To Use PPE Properly'
      case 'OPERATING_EQUIPMENT_WITHOUT_AUTHORITY': return 'Operating Equipment Without Authority'
      case 'INCORRECT_USE_OF_EQUIPMENT_OR_MACHINERY': return 'Incorrect Use of Equipment / Machinery'
      case 'USE_OF_DEFECTIVE_EQUIPMENT_OR_MACHINERY': return 'Use Of Defective Equipment / Machinery'
      case 'FAILURE_TO_FOLLOW_REPAIR_OR_MAINTENANCE_INSTRUCTIONS':
        return 'Failure To Follow Repair /Maintenance Instructions'
      case 'FAILURE_TO_WARN': return 'Failure To Warn'
      case 'FAILURE_TO_SECURE': return 'Failure To Secure'
      case 'BY_PASSING_SAFETY_DEVICE': return 'By-Passing Safety Device'
      case 'IMPROPER_POSITION_FOR_TASK': return 'Improper Position for Task'
      case 'IMPROPER_LIFTING_HANDLING_OR_STORAGE': return 'Improper Lifting, Handling or Storage'
      case 'HORSEPLAY_OR_INAPPROPRIATE_BEHAVIOUR': return 'Horseplay / Inappropriate Behaviour'
      case 'UNDER_INFLUENCE_OF_ALCOHOL_OR_DRUGS': return 'Under Influence of Alcohol or Drugs'
      default: return a
    }
  }
  return acts.map(mapOne).join(', ')
}

const substandardConditionsLabel = (conds?: NearMissSubstandardCondition[] | null) => {
  if (!conds || !conds.length) return '-'
  const mapOne = (c: NearMissSubstandardCondition) => {
    switch (c) {
      case 'INADEQUATE_GUARDS_OR_BARRIERS': return 'Inadequate Guards or Barriers'
      case 'INADEQUATE_OR_DEFECTIVE_PPE': return 'Inadequate Or Defective PPE'
      case 'DEFECTIVE_EQUIPMENT_OR_MACHINERY': return 'Defective Equipment / Machinery'
      case 'UNFAVOURABLE_HULL_OR_STRUCTURE_CONDITION': return 'Unfavourable Hull or Structure Condition'
      case 'POOR_HOUSEKEEPING': return 'Poor Housekeeping'
      case 'CONGESTION_OR_RESTRICTED_ACTION': return 'Congestion or Restricted Action'
      case 'INADEQUATE_OR_EXCESS_ILLUMINATION': return 'Inadequate or Excess Illumination'
      case 'INADEQUATE_VENTILATION': return 'Inadequate Ventilation'
      case 'OTHER_FACTORS': return 'Other Factors'
      default: return c
    }
  }
  return conds.map(mapOne).join(', ')
}

const nearMissStatusLabel = (s?: NearMissStatus | null) => {
  switch (s) {
    case 'SAVE': return 'Saved'
    case 'SUBMIT': return 'Submitted'
    case 'CLOSE': return 'Closed'
    default: return '-'
  }
}

const nearMissClosureResultLabel = (
  status?: NearMissStatus | null,
  closureResult?: NearMissClosureResult | null
) => {
  if (status !== 'CLOSE') return undefined

  switch (closureResult) {
    case 'TO_BE_REVIEWED_NEXT_INSPECTION':
      return 'Closed and TBR'
    case 'SATISFACTORY':
    default:
      return 'Closed Satisfactorily'
  }
}

/** Raw list from server */
export async function searchNearMissReports(
  params: SearchNearMissParams
): Promise<QHSENearMissReportDto[]> {
  const { data } = await axios.get<QHSENearMissReportDto[]>(NEAR_MISS_API_URL, { params })
  return Array.isArray(data) ? data : []
}

/** Map DTO -> UI row for table */
export function mapNearMissToRow(
  d: QHSENearMissReportDto,
  vesselMeta?: { companyGroupAdminId?: number | null; companyAdminId?: number | null }
): NearMissRecord {
  const status: NearMissStatus | undefined =
    (d.status as NearMissStatus | undefined) ?? 'SAVE'

  const occs = Array.isArray(d.occurrenceTypes) ? d.occurrenceTypes : []
  const occLabel = occs.length
    ? occs.map(occurrenceTypeLabel).join(', ')
    : '-'

  return {
    id: Number(d.id),
    vesselId: Number(d.vesselId),
    vesselName: d.vesselName || '-',

    reportNumber: d.reportNumber ?? null,
    locationPosition: d.locationPosition ?? null,

    reportIssuedBy: d.reportIssuedBy ?? null,
    dateIssued: d.dateIssued ?? null,

    dateOfOccurrence: d.dateOfOccurrence ?? null,
    timeOfOccurrence: d.timeOfOccurrence ?? null,

    reportedBy: d.reportedBy ?? null,
    reportedTo: d.reportedTo ?? null,
    dateReported: d.dateReported ?? null,
    timeReported: d.timeReported ?? null,

    occurrenceTypes: occs as NearMissOccurrenceType[],
    occurrenceTypesLabel: occLabel,

    personalInjuryFormCompleted: d.personalInjuryFormCompleted ?? null,
    anyStatementsAttached: d.anyStatementsAttached ?? null,

    locationOfOccurrence: d.locationOfOccurrence ?? null,
    locationOfOccurrenceLabel: locationOfOccurrenceLabel(d.locationOfOccurrence ?? undefined),

    descriptionOfOccurrence: d.descriptionOfOccurrence ?? null,

    substandardActs: d.substandardActs ?? null,
    substandardActsLabel: substandardActsLabel(d.substandardActs ?? undefined),

    substandardConditions: d.substandardConditions ?? null,
    substandardConditionsLabel: substandardConditionsLabel(
      d.substandardConditions ?? undefined
    ),

    status,
    statusLabel: nearMissStatusLabel(status),
    closureResult: d.closureResult ?? null,
    closureResultLabel: nearMissClosureResultLabel(status, d.closureResult ?? null),

    supportingDocsPrimaryPath: d.supportingDocsPrimaryPath ?? null,

    companyGroupAdminId: vesselMeta?.companyGroupAdminId ?? null,
    companyAdminId: vesselMeta?.companyAdminId ?? null,
  }
}

/** Create near miss (JSON, no files yet) */
export async function createNearMissReport(
  payload: Omit<QHSENearMissReportDto,
    'id' | 'createdAt' | 'updatedAt' | 'vesselName' | 'reportNumber' | 'status'>
): Promise<QHSENearMissReportDto> {
  const { data } = await axios.post<QHSENearMissReportDto>(NEAR_MISS_API_URL, payload)
  return data
}

/** Get one near miss */
export async function getNearMissReport(id: number): Promise<QHSENearMissReportDto> {
  const { data } = await axios.get<QHSENearMissReportDto>(`${NEAR_MISS_API_URL}/${id}`)
  return data
}

/** Update near miss */
export async function updateNearMissReport(
  id: number,
  partial: Partial<Omit<QHSENearMissReportDto,
    'id' | 'createdAt' | 'updatedAt' | 'vesselName' | 'reportNumber' | 'status'>>
): Promise<QHSENearMissReportDto> {
  const { data } = await axios.put<QHSENearMissReportDto>(
    `${NEAR_MISS_API_URL}/${id}`,
    partial
  )
  return data
}

/** Submit (SAVE -> SUBMIT) */
export async function submitNearMissReport(id: number): Promise<QHSENearMissReportDto> {
  const { data } = await axios.post<QHSENearMissReportDto>(`${NEAR_MISS_API_URL}/${id}/submit`)
  return data
}

/** Close (SUBMIT -> CLOSE) with closure result */
export async function closeNearMissReport(
  id: number,
  result?: NearMissClosureResult
): Promise<QHSENearMissReportDto> {
  const { data } = await axios.post<QHSENearMissReportDto>(
    `${NEAR_MISS_API_URL}/${id}/close`,
    null,
    { params: result ? { result } : undefined }
  )
  return data
}

/** Hard delete */
export async function deleteNearMissReport(id: number): Promise<void> {
  await axios.delete(`${NEAR_MISS_API_URL}/${id}`)
}

/** Soft delete */
export async function softDeleteNearMissReport(id: number): Promise<void> {
  await axios.post(`${NEAR_MISS_API_URL}/${id}/soft-delete`)
}

// /** Upload / replace supporting documents (multi-file) */
// export async function uploadNearMissSupportingDocuments(
//   id: number,
//   files: File | File[]
// ): Promise<QHSENearMissReportDto> {
//   const fd = new FormData()
//   const arr = Array.isArray(files) ? files : [files]
//   arr.forEach((file) => {
//     fd.append('files', file)
//   })

//   const { data } = await axios.post<QHSENearMissReportDto>(
//     `${NEAR_MISS_API_URL}/${id}/supporting-documents`,
//     fd,
//     {
//       headers: { 'Content-Type': 'multipart/form-data' },
//       transformRequest: (d) => d,
//       maxBodyLength: Infinity,
//     }
//   )
//   return data
// }

/** View/download URLs */

export const nearMissSupportingViewUrl = (id: number) =>
  `${NEAR_MISS_API_URL}/${id}/supporting/view`

export const nearMissSupportingDownloadUrl = (id: number) =>
  `${NEAR_MISS_API_URL}/${id}/supporting/download`

export const nearMissSupportingFileViewUrl = (id: number, fileName: string) =>
  `${NEAR_MISS_API_URL}/${id}/supporting/view?file=${encodeURIComponent(fileName)}`

export const nearMissSupportingFileDownloadUrl = (id: number, fileName: string) =>
  `${NEAR_MISS_API_URL}/${id}/supporting/download?file=${encodeURIComponent(fileName)}`

export type NearMissAttachmentInfo = {
  fileName: string
  webPath: string
  sizeBytes: number
  mimeType?: string | null
}

/** List all supporting docs */
export async function listNearMissSupportingDocuments(
  id: number
): Promise<NearMissAttachmentInfo[]> {
  const { data } = await axios.get(`${NEAR_MISS_API_URL}/${id}/supporting-documents`)
  return Array.isArray(data) ? data : []
}

/** Delete a specific supporting doc */
export async function deleteNearMissSupportingDocument(
  id: number,
  fileName: string
): Promise<void> {
  await axios.delete(`${NEAR_MISS_API_URL}/${id}/supporting-document`, {
    params: { file: fileName },
  })
}

// Convenience variants which respect API_URL (same behaviour as “supporting*” helpers)

export const nearMissAttachmentFileViewUrl = (id: number, fileName: string) =>
  `${NEAR_MISS_API_URL}/${id}/supporting/view?file=${encodeURIComponent(fileName)}`

export const nearMissAttachmentFileDownloadUrl = (id: number, fileName: string) =>
  `${NEAR_MISS_API_URL}/${id}/supporting/download?file=${encodeURIComponent(fileName)}`

export const listNearMissAttachments = async (
  id: number
): Promise<NearMissAttachmentInfo[]> => {
  const { data } = await axios.get<NearMissAttachmentInfo[]>(
    `${NEAR_MISS_API_URL}/${id}/supporting-documents`
  )
  return Array.isArray(data) ? data : []
}

export const deleteNearMissAttachment = async (
  id: number,
  fileName: string
): Promise<void> => {
  await axios.delete(`${NEAR_MISS_API_URL}/${id}/supporting-document`, {
    params: { file: fileName },
  })
}

/** Upload supporting documents for a near miss.
 * Called from Add/Edit/Review flows.
 */
export const uploadNearMissSupportingDocuments = async (
  id: number,
  files: File[]
) => {
  const formData = new FormData()
  files.forEach((f) => formData.append('files', f))

  const { data } = await axios.post(
    `${NEAR_MISS_API_URL}/${id}/supporting-documents`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      transformRequest: (d) => d,
      maxBodyLength: Infinity,
    }
  )
  return data
}

// ===============================
// QHSE SAFETY COMMITTEE MEETINGS
// ===============================

export type SearchSafetyCommitteeParams = {
  companyGroupId?: number
  companyId?: number
  vesselId?: number
  status?: SafetyCommitteeStatus
  fromDate?: string
  toDate?: string
  activeOnly?: boolean
}

export type SafetyCommitteeAttachmentType = 'FORM' | 'CHECKLIST' | 'FINDING' | 'OFFICE'

export type SafetyCommitteeAttachmentInfo = {
  fileName: string
  webPath: string
  sizeBytes: number
  mimeType?: string | null
}

const scmAttachmentSegment = (type: SafetyCommitteeAttachmentType) => {
  switch (type) {
    case 'FORM':
      return 'form'
    case 'CHECKLIST':
      return 'checklist'
    case 'FINDING':
      return 'finding'
    case 'OFFICE':
      return 'office'
    default:
      return 'form'
  }
}

export async function searchSafetyCommitteeMeetings(
  params: SearchSafetyCommitteeParams
): Promise<QHSESafetyCommitteeMeetingDto[]> {
  const { data } = await axios.get<QHSESafetyCommitteeMeetingDto[]>(SCM_API_URL, { params })
  return Array.isArray(data) ? data : []
}

export async function getSafetyCommitteeMeeting(id: number): Promise<QHSESafetyCommitteeMeetingDto> {
  const { data } = await axios.get<QHSESafetyCommitteeMeetingDto>(`${SCM_API_URL}/${id}`)
  return data
}

export async function createSafetyCommitteeMeeting(
  payload: Omit<
    QHSESafetyCommitteeMeetingDto,
    'id' | 'createdAt' | 'updatedAt' | 'status' | 'createdByRole' | 'submittedAt' | 'officeReviewed'
  >
): Promise<QHSESafetyCommitteeMeetingDto> {
  const { data } = await axios.post<QHSESafetyCommitteeMeetingDto>(SCM_API_URL, payload)
  return data
}

export async function updateSafetyCommitteeMeeting(
  id: number,
  partial: Partial<QHSESafetyCommitteeMeetingDto>
): Promise<QHSESafetyCommitteeMeetingDto> {
  const { data } = await axios.put<QHSESafetyCommitteeMeetingDto>(`${SCM_API_URL}/${id}`, partial)
  return data
}

export async function submitSafetyCommitteeMeeting(
  id: number
): Promise<QHSESafetyCommitteeMeetingDto> {
  const { data } = await axios.post<QHSESafetyCommitteeMeetingDto>(`${SCM_API_URL}/${id}/submit`)
  return data
}

export async function closeSafetyCommitteeMeeting(
  id: number,
  result?: 'SATISFACTORY' | 'TO_BE_REVIEWED_NEXT_INSPECTION'
): Promise<QHSESafetyCommitteeMeetingDto> {
  const { data } = await axios.post<QHSESafetyCommitteeMeetingDto>(
    `${SCM_API_URL}/${id}/close`,
    null,
    { params: { result } }
  )
  return data
}

export async function uploadSafetyCommitteeAttachments(
  id: number,
  type: SafetyCommitteeAttachmentType,
  files: File[]
): Promise<QHSESafetyCommitteeMeetingDto> {
  const segment = scmAttachmentSegment(type)
  const form = new FormData()
  files.forEach((f) => form.append('files', f))

  const { data } = await axios.post<QHSESafetyCommitteeMeetingDto>(
    `${SCM_API_URL}/${id}/${segment}-attachments`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      transformRequest: (d) => d,
      maxBodyLength: Infinity,
    }
  )
  return data
}

export async function listSafetyCommitteeAttachments(
  id: number,
  type: SafetyCommitteeAttachmentType
): Promise<SafetyCommitteeAttachmentInfo[]> {
  const segment = scmAttachmentSegment(type)
  const { data } = await axios.get<SafetyCommitteeAttachmentInfo[]>(
    `${SCM_API_URL}/${id}/${segment}-attachments`
  )
  return Array.isArray(data) ? data : []
}

export async function deleteSafetyCommitteeAttachment(
  id: number,
  type: SafetyCommitteeAttachmentType,
  fileName: string
): Promise<void> {
  const segment = scmAttachmentSegment(type)
  await axios.delete(`${SCM_API_URL}/${id}/${segment}-attachment`, {
    params: { file: fileName },
  })
}

export const safetyCommitteeAttachmentViewUrl = (
  id: number,
  type: SafetyCommitteeAttachmentType,
  fileName?: string
) => {
  const segment = scmAttachmentSegment(type)
  const base = `${SCM_API_URL}/${id}/${segment}/view`
  return fileName ? `${base}?file=${encodeURIComponent(fileName)}` : base
}

export const safetyCommitteeAttachmentDownloadUrl = (
  id: number,
  type: SafetyCommitteeAttachmentType,
  fileName?: string
) => {
  const segment = scmAttachmentSegment(type)
  const base = `${SCM_API_URL}/${id}/${segment}/download`
  return fileName ? `${base}?file=${encodeURIComponent(fileName)}` : base
}

// ===============================
// QHSE WORK PERMITS & CHECKLISTS
// ===============================

export type SearchWorkPermitParams = {
  companyGroupId?: number
  companyId?: number
  vesselId?: number
  status?: 'SAVE' | 'SUBMIT' | 'CLOSE'
  fromDate?: string
  toDate?: string
  activeOnly?: boolean
}

export type WorkPermitAttachmentType = 'FORM' | 'OFFICE' | 'COMPLETED'

const workPermitAttachmentSegment = (type: WorkPermitAttachmentType) => {
  switch (type) {
    case 'FORM':
      return 'form'
    case 'OFFICE':
      return 'office'
    case 'COMPLETED':
      return 'completed'
    default:
      return 'form'
  }
}

export async function searchWorkPermits(
  params: SearchWorkPermitParams
): Promise<QHSEWorkPermitDto[]> {
  const { data } = await axios.get<QHSEWorkPermitDto[]>(WORK_PERMIT_API_URL, { params })
  return Array.isArray(data) ? data : []
}

export async function getWorkPermit(id: number): Promise<QHSEWorkPermitDto> {
  const { data } = await axios.get<QHSEWorkPermitDto>(`${WORK_PERMIT_API_URL}/${id}`)
  return data
}

export async function createWorkPermit(
  payload: Omit<
    QHSEWorkPermitDto,
    'id' | 'createdAt' | 'updatedAt' | 'status' | 'createdByRole' | 'submittedAt' | 'closedAt'
  >
): Promise<QHSEWorkPermitDto> {
  const { data } = await axios.post<QHSEWorkPermitDto>(WORK_PERMIT_API_URL, payload)
  return data
}

export async function updateWorkPermit(
  id: number,
  partial: Partial<QHSEWorkPermitDto>
): Promise<QHSEWorkPermitDto> {
  const { data } = await axios.put<QHSEWorkPermitDto>(`${WORK_PERMIT_API_URL}/${id}`, partial)
  return data
}

export async function submitWorkPermit(id: number): Promise<QHSEWorkPermitDto> {
  const { data } = await axios.post<QHSEWorkPermitDto>(`${WORK_PERMIT_API_URL}/${id}/submit`)
  return data
}

export async function approveWorkPermit(id: number): Promise<QHSEWorkPermitDto> {
  const { data } = await axios.post<QHSEWorkPermitDto>(`${WORK_PERMIT_API_URL}/${id}/approve`)
  return data
}

export async function closeWorkPermit(id: number): Promise<QHSEWorkPermitDto> {
  const { data } = await axios.post<QHSEWorkPermitDto>(`${WORK_PERMIT_API_URL}/${id}/close`)
  return data
}

export async function uploadWorkPermitAttachments(
  id: number,
  type: WorkPermitAttachmentType,
  files: File[]
): Promise<QHSEWorkPermitDto> {
  const segment = workPermitAttachmentSegment(type)
  const form = new FormData()
  files.forEach((f) => form.append('files', f))

  const { data } = await axios.post<QHSEWorkPermitDto>(
    `${WORK_PERMIT_API_URL}/${id}/${segment}-attachments`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      transformRequest: (d) => d,
      maxBodyLength: Infinity,
    }
  )
  return data
}

export async function listWorkPermitAttachments(
  id: number,
  type: WorkPermitAttachmentType
): Promise<WorkPermitAttachmentInfo[]> {
  const segment = workPermitAttachmentSegment(type)
  const { data } = await axios.get<WorkPermitAttachmentInfo[]>(
    `${WORK_PERMIT_API_URL}/${id}/${segment}-attachments`
  )
  return Array.isArray(data) ? data : []
}

export async function deleteWorkPermitAttachment(
  id: number,
  type: WorkPermitAttachmentType,
  fileName: string
): Promise<void> {
  const segment = workPermitAttachmentSegment(type)
  await axios.delete(`${WORK_PERMIT_API_URL}/${id}/${segment}-attachment`, {
    params: { file: fileName },
  })
}

export const workPermitAttachmentViewUrl = (
  id: number,
  type: WorkPermitAttachmentType,
  fileName?: string
) => {
  const segment = workPermitAttachmentSegment(type)
  const base = `${WORK_PERMIT_API_URL}/${id}/${segment}/view`
  return fileName ? `${base}?file=${encodeURIComponent(fileName)}` : base
}

export const workPermitAttachmentDownloadUrl = (
  id: number,
  type: WorkPermitAttachmentType,
  fileName?: string
) => {
  const segment = workPermitAttachmentSegment(type)
  const base = `${WORK_PERMIT_API_URL}/${id}/${segment}/download`
  return fileName ? `${base}?file=${encodeURIComponent(fileName)}` : base
}

// ===============================
// RISK ASSESSMENT FORMS (Vessel-scoped)
// ===============================

export const riskAssessmentViewUrl = (id: number) => `${RISK_API_URL}/${id}/view`
export const riskAssessmentDownloadUrl = (id: number) => `${RISK_API_URL}/${id}/download`

export const riskAssessmentRevisionViewUrl = (revisionId: number) => `${RISK_API_URL}/revisions/${revisionId}/view`
export const riskAssessmentRevisionDownloadUrl = (revisionId: number) => `${RISK_API_URL}/revisions/${revisionId}/download`

/** list by vessel */
export async function listRiskAssessmentsByVessel(vesselId: number): Promise<RiskAssessmentDto[]> {
  try {
    const { data } = await axios.get<RiskAssessmentDto[]>(`${RISK_API_URL}/vessel/${vesselId}`)
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Failed to load risk assessments'))
  }
}

/** get one */
export async function getRiskAssessment(id: number): Promise<RiskAssessmentDto> {
  try {
    const { data } = await axios.get<RiskAssessmentDto>(`${RISK_API_URL}/${id}`)
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Failed to load risk assessment'))
  }
}

/** create (multipart) – number is auto-generated by backend */
export async function createRiskAssessment(payload: {
  vesselId: number
  dateOfIssue?: string
  uploadedBy: string
  uploadedByName: string
  remarks?: string
  file: File
}) {
  const fd = new FormData()
  fd.append('vesselId', String(payload.vesselId))
  if (payload.dateOfIssue) fd.append('dateOfIssue', payload.dateOfIssue)
  fd.append('uploadedBy', payload.uploadedBy)
  fd.append('uploadedByName', payload.uploadedByName)
  if (payload.remarks) fd.append('remarks', payload.remarks)
  fd.append('file', payload.file)

  const { data } = await axios.post(`${RISK_API_URL}`, fd, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Accept: 'application/json',
    },
    transformRequest: (d) => d,
    maxBodyLength: Infinity,
  })
  return data as RiskAssessmentDto
}

/** update meta – we will NOT send assessmentNumber (kept immutable) */
export async function updateRiskAssessment(
  id: number,
  partial: Partial<RiskAssessmentDto>
): Promise<RiskAssessmentDto> {
  try {
    const { data } = await axios.put<RiskAssessmentDto>(`${RISK_API_URL}/${id}`, partial)
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Failed to update risk assessment'))
  }
}

/** replace file (new revision) */
export async function replaceRiskAssessmentFile(id: number, file: File): Promise<RiskAssessmentDto> {
  const fd = new FormData()
  fd.append('file', file)

  const { data } = await axios.patch<RiskAssessmentDto>(`${RISK_API_URL}/${id}/file`, fd, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Accept: 'application/json',
    },
    transformRequest: (d) => d,
    maxBodyLength: Infinity,
  })
  return data
}

/** soft delete */
export async function softDeleteRiskAssessment(id: number): Promise<void> {
  try {
    await axios.delete(`${RISK_API_URL}/${id}`)
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Failed to delete risk assessment'))
  }
}

/** revisions */
export async function listRiskAssessmentRevisions(id: number): Promise<RiskAssessmentRevision[]> {
  const { data } = await axios.get(`${RISK_API_URL}/${id}/revisions`)
  return data as RiskAssessmentRevision[]
}

/** DTO -> table row */
export function mapRiskAssessmentDtoToRow(d: RiskAssessmentDto): RiskAssessmentRecord {
  const fileName = d.assessmentNumber || `risk_assessment_${d.id}`
  const size = sizeToBytes(d.fileSize)
  const expiryIso = d.dateOfExpiry || undefined

  const isExpired =
    expiryIso != null ? new Date(expiryIso).getTime() < Date.now() : false

  return {
    id: d.id,
    vesselId: d.vesselId,
    vesselName: d.vesselName,
    assessmentNumber: d.assessmentNumber,
    dateOfIssue: d.dateOfIssue || undefined,
    dateOfExpiry: d.dateOfExpiry || undefined,
    isExpired,
    uploadedByName: d.uploadedByName || '',
    uploadedDate: d.uploadedDate || '',
    file: {
      name: fileName,
      url: riskAssessmentDownloadUrl(d.id),
      type: d.fileMime || 'application/octet-stream',
      size,
    },
    remarks: d.remarks || undefined,
    revisionCount: Number(d.revisionCount ?? 0),
  }
}

// --- Risk Assessment Library (SAF) ---

export async function listRiskLibraryCategories(): Promise<RiskLibraryCategory[]> {
  try {
    const { data } = await axios.get<RiskLibraryCategory[]>(`${RISK_LIBRARY_API_URL}/categories`)
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Access denied to risk library categories'))
  }
}

export async function listRiskLibraryTasks(
  categoryId: number,
  forCompanyGroupId?: number | null,
  forVesselId?: number | null
): Promise<RiskLibraryTaskSummary[]> {
  try {
    const { data } = await axios.get<RiskLibraryTaskSummary[]>(`${RISK_LIBRARY_API_URL}/tasks`, {
      params: {
        categoryId,
        ...(forCompanyGroupId != null ? { forCompanyGroupId } : {}),
        ...(forVesselId != null ? { forVesselId } : {}),
      },
    })
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Access denied to risk library tasks'))
  }
}

export async function listRiskLibraryRiskNumberOptions(
  excludeCriticalEquipment: boolean,
  forCompanyGroupId?: number | null,
  forVesselId?: number | null
): Promise<RiskLibraryRiskNumberOption[]> {
  const { data } = await axios.get<RiskLibraryRiskNumberOption[]>(`${RISK_LIBRARY_API_URL}/tasks/risk-number-options`, {
    params: {
      excludeCriticalEquipment,
      ...(forCompanyGroupId != null ? { forCompanyGroupId } : {}),
      ...(forVesselId != null ? { forVesselId } : {}),
    },
  })
  return data
}

export async function resolveRiskLibraryRiskNumber(
  riskNumber: string,
  forCompanyGroupId?: number | null,
  forVesselId?: number | null
): Promise<RiskLibraryRiskNumberOption> {
  const { data } = await axios.get<RiskLibraryRiskNumberOption>(`${RISK_LIBRARY_API_URL}/tasks/resolve-risk-number`, {
    params: {
      riskNumber,
      ...(forCompanyGroupId != null ? { forCompanyGroupId } : {}),
      ...(forVesselId != null ? { forVesselId } : {}),
    },
  })
  return data
}

export async function listRiskLibraryCategoryTaskDetails(
  categoryId: number,
  forCompanyGroupId?: number | null,
  forVesselId?: number | null
): Promise<RiskLibraryTaskDetail[]> {
  const { data } = await axios.get<RiskLibraryTaskDetail[]>(`${RISK_LIBRARY_API_URL}/tasks/category-details`, {
    params: {
      categoryId,
      ...(forCompanyGroupId != null ? { forCompanyGroupId } : {}),
      ...(forVesselId != null ? { forVesselId } : {}),
    },
  })
  return data
}

export async function listRiskLibraryVesselOptions(
  forCompanyGroupId?: number | null
): Promise<RiskLibraryVesselOption[]> {
  const { data } = await axios.get<RiskLibraryVesselOption[]>(`${RISK_LIBRARY_API_URL}/vessel-options`, {
    params: forCompanyGroupId != null ? { forCompanyGroupId } : undefined,
  })
  return data
}

export async function promoteRiskLibraryTaskToCompanyScope(
  taskId: number,
  forCompanyGroupId?: number | null
): Promise<RiskLibraryTaskDetail> {
  const { data } = await axios.post<RiskLibraryTaskDetail>(
    `${RISK_LIBRARY_API_URL}/tasks/${taskId}/promote-to-company-scope`,
    {},
    {
      params: forCompanyGroupId != null ? { forCompanyGroupId } : undefined,
    }
  )
  return data
}

export async function getRiskLibraryTask(id: number): Promise<RiskLibraryTaskDetail> {
  try {
    const { data } = await axios.get<RiskLibraryTaskDetail>(`${RISK_LIBRARY_API_URL}/tasks/${id}`)
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Access denied to risk library task'))
  }
}

export async function createRiskLibraryTask(body: RiskLibraryTaskWritePayload): Promise<RiskLibraryTaskDetail> {
  try {
    const { data } = await axios.post<RiskLibraryTaskDetail>(`${RISK_LIBRARY_API_URL}/tasks`, body)
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Access denied to create risk library task'))
  }
}

export async function updateRiskLibraryTask(
  id: number,
  body: RiskLibraryTaskWritePayload
): Promise<RiskLibraryTaskDetail> {
  try {
    const { data } = await axios.put<RiskLibraryTaskDetail>(`${RISK_LIBRARY_API_URL}/tasks/${id}`, body)
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Access denied to update risk library task'))
  }
}

export async function deleteRiskLibraryTask(id: number): Promise<void> {
  try {
    await axios.delete(`${RISK_LIBRARY_API_URL}/tasks/${id}`)
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Access denied to delete risk library task'))
  }
}

export async function getRiskLibraryCompanyHeader(
  forCompanyGroupId?: number | null
): Promise<RiskLibraryCompanyHeader> {
  try {
    const { data } = await axios.get<RiskLibraryCompanyHeader>(`${RISK_LIBRARY_API_URL}/company-header`, {
      params: forCompanyGroupId != null ? { forCompanyGroupId } : undefined,
    })
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Access denied to risk library header'))
  }
}

export async function saveRiskLibraryCompanyHeader(
  body: Omit<RiskLibraryCompanyHeader, 'revision'>,
  forCompanyGroupId?: number | null
): Promise<RiskLibraryCompanyHeader> {
  try {
    const { data } = await axios.put<RiskLibraryCompanyHeader>(`${RISK_LIBRARY_API_URL}/company-header`, body, {
      params: forCompanyGroupId != null ? { forCompanyGroupId } : undefined,
    })
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Access denied to save risk library header'))
  }
}

// ============================================================================
// Month End Library – Main Engine Consumption
// ============================================================================

const ME_CONSUMPTION_API_URL = `${API_URL}/qhse/month-end-reports/main-engine-consumption`

export async function searchMainEngineConsumption(params: {
  companyGroupId?: number
  companyAdminId?: number
  vesselId?: number
  month?: number
  year?: number
  activeOnly?: boolean
}): Promise<MainEngineConsumptionDto[]> {
  const { data } = await axios.get<MainEngineConsumptionDto[]>(ME_CONSUMPTION_API_URL, { params })
  return data
}

export async function getMainEngineConsumption(id: number): Promise<MainEngineConsumptionDto> {
  const { data } = await axios.get<MainEngineConsumptionDto>(`${ME_CONSUMPTION_API_URL}/${id}`)
  return data
}

export async function getMainEngineConsumptionDefaults(vesselId: number): Promise<MainEngineConsumptionDto> {
  const { data } = await axios.get<MainEngineConsumptionDto>(`${ME_CONSUMPTION_API_URL}/defaults`, {
    params: { vesselId },
  })
  return data
}

export async function createMainEngineConsumption(
  dto: Partial<MainEngineConsumptionDto>
): Promise<MainEngineConsumptionDto> {
  const { data } = await axios.post<MainEngineConsumptionDto>(ME_CONSUMPTION_API_URL, dto)
  return data
}

export async function updateMainEngineConsumption(
  id: number,
  dto: Partial<MainEngineConsumptionDto>
): Promise<MainEngineConsumptionDto> {
  const { data } = await axios.put<MainEngineConsumptionDto>(`${ME_CONSUMPTION_API_URL}/${id}`, dto)
  return data
}

export async function deleteMainEngineConsumption(id: number): Promise<void> {
  await axios.delete(`${ME_CONSUMPTION_API_URL}/${id}`)
}

export async function softDeleteMainEngineConsumption(id: number): Promise<void> {
  await axios.post(`${ME_CONSUMPTION_API_URL}/${id}/soft-delete`)
}

// ---- Month End Report Revisions ----

export const monthEndReportRevisionViewUrl = (
  revisionId: number,
  reportType: 'main-engine-consumption' | 'machinery-status'
) => `${API_URL}/qhse/month-end-reports/${reportType}/revisions/${revisionId}/view`

export const monthEndReportRevisionDownloadUrl = (
  revisionId: number,
  reportType: 'main-engine-consumption' | 'machinery-status'
) => `${API_URL}/qhse/month-end-reports/${reportType}/revisions/${revisionId}/download`

export async function listMonthEndReportRevisions(
  id: number,
  reportType: 'main-engine-consumption' | 'machinery-status'
) {
  const { data } = await axios.get(
    `${API_URL}/qhse/month-end-reports/${reportType}/${id}/revisions`
  )
  return data as Array<{
    id: number
    revisionIndex: number
    filePath: string
    fileSize: string
    fileMime: string | null
    uploadedBy: number
    uploadedByName: string
    uploadedDate: string
    createdAt: string
  }>
}

// ---------- Main Engine Trial Attachments (Seatrial / Shoptrial) – per-vessel one-time ----------

const ME_TRIAL_ATTACH_URL = `${ME_CONSUMPTION_API_URL}/trial-attachments`

export async function listMainEngineTrialAttachments(
  vesselId: number,
  trialType: MainEngineTrialType
): Promise<MainEngineTrialAttachmentDto[]> {
  const { data } = await axios.get<MainEngineTrialAttachmentDto[]>(ME_TRIAL_ATTACH_URL, {
    params: { vesselId, trialType },
  })
  return data
}

export async function uploadMainEngineTrialAttachment(
  vesselId: number,
  trialType: MainEngineTrialType,
  file: File
): Promise<MainEngineTrialAttachmentDto> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await axios.post<MainEngineTrialAttachmentDto>(ME_TRIAL_ATTACH_URL, form, {
    params: { vesselId, trialType },
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function deleteMainEngineTrialAttachment(id: number): Promise<void> {
  await axios.delete(`${ME_TRIAL_ATTACH_URL}/${id}`)
}

export function mainEngineTrialAttachmentViewUrl(id: number): string {
  return `${ME_TRIAL_ATTACH_URL}/${id}/view`
}

export function mainEngineTrialAttachmentDownloadUrl(id: number): string {
  return `${ME_TRIAL_ATTACH_URL}/${id}/download`
}

// ============================================================================
// Month End Library – Machinery Status (7 report types)
// ============================================================================

type MachinerySearchParams = {
  companyGroupId?: number
  companyAdminId?: number
  vesselId?: number
  month?: number
  year?: number
  activeOnly?: boolean
}

function machineryApi(segment: string) {
  const base = `${API_URL}/qhse/month-end-reports/machinery-status/${segment}`
  return {
    search: async (params: MachinerySearchParams) => {
      const { data } = await axios.get(base, { params })
      return data
    },
    get: async (id: number) => {
      const { data } = await axios.get(`${base}/${id}`)
      return data
    },
    defaults: async (vesselId: number) => {
      const { data } = await axios.get(`${base}/defaults`, { params: { vesselId } })
      return data
    },
    create: async (dto: any) => {
      const { data } = await axios.post(base, dto)
      return data
    },
    update: async (id: number, dto: any) => {
      const { data } = await axios.put(`${base}/${id}`, dto)
      return data
    },
    remove: async (id: number) => {
      await axios.delete(`${base}/${id}`)
    },
  }
}

// 1. Auxiliary Engine
const auxEngineApi = machineryApi('auxiliary-engine')
export const searchAuxiliaryEngineReports = (p: MachinerySearchParams): Promise<AuxiliaryEngineReportDto[]> => auxEngineApi.search(p)
export const getAuxiliaryEngineReportDefaults = (vesselId: number): Promise<AuxiliaryEngineReportDto> => auxEngineApi.defaults(vesselId)
export const createAuxiliaryEngineReport = (dto: Partial<AuxiliaryEngineReportDto>): Promise<AuxiliaryEngineReportDto> => auxEngineApi.create(dto)
export const updateAuxiliaryEngineReport = (id: number, dto: Partial<AuxiliaryEngineReportDto>): Promise<AuxiliaryEngineReportDto> => auxEngineApi.update(id, dto)
export const deleteAuxiliaryEngineReport = (id: number): Promise<void> => auxEngineApi.remove(id)

// 2. Fresh Water Generator
const fwgApi = machineryApi('fresh-water-generator')
export const searchFreshWaterGeneratorReports = (p: MachinerySearchParams): Promise<FreshWaterGeneratorReportDto[]> => fwgApi.search(p)
export const getFreshWaterGeneratorReportDefaults = (vesselId: number): Promise<FreshWaterGeneratorReportDto> => fwgApi.defaults(vesselId)
export const createFreshWaterGeneratorReport = (dto: Partial<FreshWaterGeneratorReportDto>): Promise<FreshWaterGeneratorReportDto> => fwgApi.create(dto)
export const updateFreshWaterGeneratorReport = (id: number, dto: Partial<FreshWaterGeneratorReportDto>): Promise<FreshWaterGeneratorReportDto> => fwgApi.update(id, dto)
export const deleteFreshWaterGeneratorReport = (id: number): Promise<void> => fwgApi.remove(id)

// 3. BWTS
const bwtsApi = machineryApi('bwts')
export const searchBwtsReports = (p: MachinerySearchParams): Promise<BwtsReportDto[]> => bwtsApi.search(p)
export const getBwtsReportDefaults = (vesselId: number): Promise<BwtsReportDto> => bwtsApi.defaults(vesselId)
export const createBwtsReport = (dto: Partial<BwtsReportDto>): Promise<BwtsReportDto> => bwtsApi.create(dto)
export const updateBwtsReport = (id: number, dto: Partial<BwtsReportDto>): Promise<BwtsReportDto> => bwtsApi.update(id, dto)
export const deleteBwtsReport = (id: number): Promise<void> => bwtsApi.remove(id)

// 4. Purifier
const purifierApi = machineryApi('purifier')
export const searchPurifierReports = (p: MachinerySearchParams): Promise<PurifierReportDto[]> => purifierApi.search(p)
export const getPurifierReportDefaults = (vesselId: number): Promise<PurifierReportDto> => purifierApi.defaults(vesselId)
export const createPurifierReport = (dto: Partial<PurifierReportDto>): Promise<PurifierReportDto> => purifierApi.create(dto)
export const updatePurifierReport = (id: number, dto: Partial<PurifierReportDto>): Promise<PurifierReportDto> => purifierApi.update(id, dto)
export const deletePurifierReport = (id: number): Promise<void> => purifierApi.remove(id)

// 5. Inert Gas System
const igsApi = machineryApi('inert-gas-system')
export const searchInertGasSystemReports = (p: MachinerySearchParams): Promise<InertGasSystemReportDto[]> => igsApi.search(p)
export const getInertGasSystemReportDefaults = (vesselId: number): Promise<InertGasSystemReportDto> => igsApi.defaults(vesselId)
export const createInertGasSystemReport = (dto: Partial<InertGasSystemReportDto>): Promise<InertGasSystemReportDto> => igsApi.create(dto)
export const updateInertGasSystemReport = (id: number, dto: Partial<InertGasSystemReportDto>): Promise<InertGasSystemReportDto> => igsApi.update(id, dto)
export const deleteInertGasSystemReport = (id: number): Promise<void> => igsApi.remove(id)

// 6. Compressor System
const compressorApi = machineryApi('compressor-system')
export const searchCompressorSystemReports = (p: MachinerySearchParams): Promise<CompressorSystemReportDto[]> => compressorApi.search(p)
export const getCompressorSystemReportDefaults = (vesselId: number): Promise<CompressorSystemReportDto> => compressorApi.defaults(vesselId)
export const createCompressorSystemReport = (dto: Partial<CompressorSystemReportDto>): Promise<CompressorSystemReportDto> => compressorApi.create(dto)
export const updateCompressorSystemReport = (id: number, dto: Partial<CompressorSystemReportDto>): Promise<CompressorSystemReportDto> => compressorApi.update(id, dto)
export const deleteCompressorSystemReport = (id: number): Promise<void> => compressorApi.remove(id)

// 7. Boiler Performance
const boilerApi = machineryApi('boiler-performance')
export const searchBoilerPerformanceReports = (p: MachinerySearchParams): Promise<BoilerPerformanceReportDto[]> => boilerApi.search(p)
export const getBoilerPerformanceReportDefaults = (vesselId: number): Promise<BoilerPerformanceReportDto> => boilerApi.defaults(vesselId)
export const createBoilerPerformanceReport = (dto: Partial<BoilerPerformanceReportDto>): Promise<BoilerPerformanceReportDto> => boilerApi.create(dto)
export const updateBoilerPerformanceReport = (id: number, dto: Partial<BoilerPerformanceReportDto>): Promise<BoilerPerformanceReportDto> => boilerApi.update(id, dto)
export const deleteBoilerPerformanceReport = (id: number): Promise<void> => boilerApi.remove(id)

// ============================================================================
// Module 1 Reports (Month End Report Names + uploaded reports)
// ============================================================================

const MODULE1_REPORT_API_URL = `${API_URL}/qhse/module1-report-names`
const MODULE1_UPLOADED_REPORT_API_URL = `${API_URL}/qhse/module1-reports`

export const module1ReportRevisionViewUrl = (revisionId: number) =>
  `${MODULE1_UPLOADED_REPORT_API_URL}/revisions/${revisionId}/view`

export const module1ReportRevisionDownloadUrl = (revisionId: number) =>
  `${MODULE1_UPLOADED_REPORT_API_URL}/revisions/${revisionId}/download`

export async function listModule1ReportNames(): Promise<any[]> {
  try {
    const { data } = await axios.get(MODULE1_REPORT_API_URL)
    return data || []
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Failed to load month-end reports'))
  }
}

export async function getModule1ReportName(id: number): Promise<any> {
  const { data } = await axios.get(`${MODULE1_REPORT_API_URL}/${id}`)
  return data
}

export async function createModule1ReportName(payload: any): Promise<any> {
  const { data } = await axios.post(MODULE1_REPORT_API_URL, payload)
  return data
}

export async function updateModule1ReportName(id: number, payload: any): Promise<any> {
  const { data } = await axios.put(`${MODULE1_REPORT_API_URL}/${id}`, payload)
  return data
}

export async function deleteModule1ReportName(id: number): Promise<void> {
  await axios.delete(`${MODULE1_REPORT_API_URL}/${id}`)
}

export async function listModule1ReportRevisions(id: number) {
  const { data } = await axios.get(`${MODULE1_UPLOADED_REPORT_API_URL}/${id}/revisions`)
  return data as Array<{
    id: number
    revisionIndex: number
    filePath: string
    fileSize: string
    fileMime: string | null
    uploadedBy: number
    uploadedByName: string
    uploadedDate: string
    createdAt: string
  }>
}

export async function updateModule1ReportRemarks(id: number, remarks: string) {
  try {
    const { data } = await axios.patch(`${MODULE1_UPLOADED_REPORT_API_URL}/${id}/remarks`, { remarks })
    return data
  } catch (err) {
    // fallback to PUT if PATCH isn't supported
    try {
      const { data } = await axios.put(`${MODULE1_UPLOADED_REPORT_API_URL}/${id}`, { remarks })
      return data
    } catch (putErr) {
      throw new Error(pickAxiosMessage(putErr || err, 'Failed to update remarks'))
    }
  }
}

export async function updateModule1ReportReviewNote(id: number, reviewNote: string) {
  try {
    const { data } = await axios.patch(`${MODULE1_UPLOADED_REPORT_API_URL}/${id}/review-note`, { reviewNote })
    return data
  } catch (err) {
    throw new Error(pickAxiosMessage(err, 'Failed to update review note'))
  }
}

